// Встроенная инструкция MP-WMS: по разделу на каждый пункт меню.
// Тексты — для кладовщиков и менеджеров, названия кнопок и вкладок — как на экране, в «кавычках».
// routes — начала адресов страниц: по ним справка открывается на нужном разделе.

export interface HelpStep { title: string; text: string; image?: string; imageAlt?: string }
export interface HelpSection {
  id: string;            // латинский идентификатор раздела
  title: string;         // заголовок раздела
  group: string;         // группа меню
  routes: string[];      // адреса страниц, которые объясняет раздел
  roles: string;         // кто работает на этом экране
  summary: string;       // 1–2 предложения: для чего экран
  steps: HelpStep[];     // порядок действий
  tips?: string[];       // важные правила и подводные камни
}

export const HELP: HelpSection[] = [
  // ================================================================ НАЧАЛО РАБОТЫ
  {
    id: 'start',
    title: 'Начало работы',
    group: 'Начало работы',
    routes: ['/login'],
    roles: 'все сотрудники',
    summary: 'Как войти в программу, как устроено верхнее меню и кто что может делать. MP-WMS ведёт склад для заказов Wildberries и Ozon: закупки, приёмку, остатки, сборку и отгрузку.',
    steps: [
      {
        title: 'Вход',
        text: 'Откройте адрес программы. Введите «Email» и «Пароль», которые выдал администратор. В поле «Устройство» можно написать, откуда вы входите, например «ТСД-2» или «ПК менеджера» — это видно в списке сеансов. Нажмите «Войти». Менеджер и администратор попадают на «Главную», кладовщик — сразу в режим ТСД (крупные кнопки для сканера). Пока ТСД не используется, основная работа по заказам идёт с компьютера под учётной записью менеджера.',
      },
      {
        title: 'Верхняя строка',
        text: 'Слева — значок связи: «онлайн» (изменения коллег появляются сами), «подключение» или «нет связи» (данные на экране могут быть устаревшими). В середине — поиск «Товар, артикул, штрихкод…»: введите текст и нажмите Enter, откроются «Остатки» с этим фильтром. Справа — ☀/☁ (включить или выключить добрые слова после сделанной работы), значок ⚠ с красным счётчиком (открывает «Центр проблем»), выбор склада, ваше имя и роль, кнопка «Выйти».',
      },
      {
        title: 'Меню разделов',
        text: 'Вторая строка — разделы в порядке движения товара: «Главная», «Закупки», «Приёмка», «Остатки», «Заказы FBS», «Поставки FBO / FBW», «Справочники», «Настройки». Наведите мышь или щёлкните по разделу — выпадет список его страниц. Раздел из одной страницы («Главная», «Поставки FBO / FBW») открывается сразу. На телефоне меню открывается кнопкой ☰ слева вверху.',
      },
      {
        title: 'Выбор склада',
        text: 'Если складов несколько, справа вверху есть список «Склад». Все страницы (остатки, заказы, приёмка) показывают выбранный склад. Если сотрудник привязан к одному складу, выбора нет — склад просто подписан.',
      },
      {
        title: 'Роли',
        text: 'Администратор — всё, включая «Настройки» (сотрудники, склады, ключи кабинетов) и «Режим ТСД». Менеджер — закупки, заказы, товары, контрагенты, решения по браку, проведение документов, утверждение инвентаризации. Кладовщик — приёмка, возвраты, остатки, сборка, упаковка, отгрузка, накладные, короба поставок; решения по деньгам и остаткам за ним не закреплены — на таких кнопках видно «решает менеджер».',
      },
      {
        title: 'Справка',
        text: 'На каждой странице есть подсказка под заголовком. Эта инструкция открывается на разделе той страницы, где вы находитесь.',
      },
    ],
    tips: [
      'Программа только читает данные из кабинетов WB и Ozon. Ничего не отправляется в кабинеты: добавить задание в поставку WB, нажать «Собрать» в Ozon, отменить заказ на площадке — это делается руками в кабинете площадки.',
      'Выгрузка остатков на площадки сейчас выключена. Колонка «Опубликовано» в «Остатках» поэтому пустая.',
      'Поиск товаров (строка поиска в шапке, «Остатки», «Товары и комплекты», «Что заказать», «Связи с WB / Ozon») не различает большие и маленькие буквы: «вишенка», «Вишенка» и «ВИШЕНКА» найдут одно и то же. Буквы «ё» и «е» тоже считаются одинаковыми.',
      'Если сессия истекла, после входа программа вернёт вас на ту же страницу.',
      'Ошибиться страшно не нужно: почти всё, что меняет остаток, требует причину и попадает в журнал действий.',
    ],
  },
  {
    id: 'typical-day',
    title: 'Типовой день: от закупки до отгрузки',
    group: 'Начало работы',
    routes: [],
    roles: 'менеджер, комплектовщик, администратор',
    summary: 'Весь путь товара по порядку: что делает менеджер за компьютером, что делает комплектовщик с бумажным листом и что нужно сделать руками в кабинетах WB и Ozon. ТСД пока не используется — заказы собирают по распечатанному листу подбора.',
    steps: [
      {
        title: '1. Утро: «Главная»',
        text: 'Менеджер открывает «Главную». Сначала блок «Срочно» (просрочки, заказы без резерва, ошибки обмена, недобор), потом «Заказы FBS», «Закупки и приёмка», «Остатки и инвентаризация». Каждая плитка ведёт в нужную очередь.',
      },
      {
        title: '2. Посмотреть заказы',
        text: 'Новые заказы WB и Ozon программа забирает сама, каждую минуту, круглосуточно, и сразу резервирует под них товар. Откройте «Заказы FBS → Заказы»; если нужно не ждать — «Обновить сейчас». Проверьте вкладку «Проблемы»: заказы без резерва, без связи с нашим товаром. Связь чинится в «Справочники → Связи с WB / Ozon».',
      },
      {
        title: '3. Кабинет WB — до печати',
        text: 'WB даёт стикер только на задание, добавленное в поставку. Поэтому в кабинете WB сначала добавьте в поставку задания, которые будете собирать.',
      },
      {
        title: '4. Создать лист подбора',
        text: 'На странице «Заказы» отметьте заказы галочками → «Лист подбора из выбранных (N)». Или «Собрать волну» → выберите срок, например «до 12:00 сегодня». Листы WB и Ozon всегда отдельные.',
      },
      {
        title: '5. Кабинет Ozon — до печати этикеток',
        text: 'Ozon даёт этикетку только на собранное в его кабинете отправление. Для отправлений из листа нажмите в кабинете Ozon «Собрать» — они перейдут в «Ожидают отгрузки».',
      },
      {
        title: '6. Распечатать лист и этикетки',
        text: 'После «Лист подбора из выбранных» страница листа открывается сама (или «Заказы FBS → Листы подбора» → «Открыть»). Вверху страницы листа: «Печать» — бумажный лист подбора, «Этикетки (N)» — стикеры WB или этикетки Ozon на все заказы листа.',
      },
      {
        title: '7. Сборка комплектовщиком',
        text: 'Отдайте лист и этикетки комплектовщику. Он ходит по ячейкам из листа, собирает каждый заказ в отдельный пакет, клеит на него этикетку с номером этого заказа и ставит отметку в квадратике листа. Не нашёл товар — пишет на листе, сколько не хватило.',
      },
      {
        title: '8. Отметить лист собранным',
        text: 'Менеджер открывает лист («Листы подбора» → «Открыть») → «Собрано по листу» → проверить список → «Всё собрано». Заказы становятся «собран», товар по учёту — в зоне отгрузки. Если чего-то не нашли — не закрывайте весь лист, а отметьте строки по одной (щёлкнуть по строке).',
      },
      {
        title: '9. Упаковка и передача курьеру',
        text: '«Заказы FBS → Упаковка» → площадка → отметить заказы → «Упаковано (N)». Когда груз забирают — отметить заказы → «Передать курьеру (N)» → подтвердить. Товар списывается, появляется документ в «Отгрузки».',
      },
      {
        title: '10. Кабинеты и накладная',
        text: 'WB: в кабинете передайте поставку в доставку и распечатайте QR-код поставки. Ozon: в кабинете сформируйте отгрузку и акт. Затем «Заказы FBS → Накладные» → «Создать накладную» по этой отгрузке → «Создать и распечатать». Подробно по шагам — разделы «Заказ FBS Wildberries…» и «Заказ FBS Ozon…».',
      },
      {
        title: '11. Закупка',
        text: '«Закупки → Что заказать» → проверить количество → «Создать заказ поставщику». В заказе нажать «Отправлен поставщику» и указать дату. Товар начинает считаться «в пути».',
      },
      {
        title: '12. Приёмка',
        text: 'Когда поставщик привёз: «Приёмка → Приёмка товара → Ожидаются» → «Начать приёмку» → сканировать товар → «Завершить приёмку». Только после этого товар идёт в продажу. Брак и неизвестные штрихкоды разбирает менеджер во вкладке «Брак и проверка».',
      },
      {
        title: '13. Возвраты и отмены',
        text: 'Посылки от покупателей — «Приёмка → Возвраты от покупателей». Если заказ отменили уже собранным, подтверждать ничего не нужно: товар сразу снова в продаже, в ячейке, откуда его собрали, — комплектовщик просто забирает пакет из зоны отгрузки и раскладывает товар на место.',
      },
      {
        title: '14. Поставки на склады площадок (FBO / FBW)',
        text: '«Поставки FBO / FBW → Что довезти» → «Создать черновик поставки» → «Зарезервировать» → разложить по коробам → «Отгружено». Когда площадка приняла — сверить принятое.',
      },
      {
        title: '15. Конец дня',
        text: 'Ещё раз «Главная»: всё ли отгружено, нет ли «Отгружены, но площадка не приняла». В «Отгрузки» менеджер скачивает «Документы для 1С за период».',
      },
    ],
    tips: [
      'Резерв ставится автоматически при загрузке заказа. Товар в резерве другим заказам не достаётся.',
      'Незавершённая приёмка и брак не продаются, даже если физически лежат на складе. Товар отменённого собранного заказа, наоборот, сразу снова в продаже — не забудьте физически вернуть его из зоны отгрузки в ячейку.',
      'Страница листа с «Этикетки (N)» и «Собрано по листу» открывается сама после создания листа, а позже — «Листы подбора» → кнопка «Открыть» в строке листа.',
      'Не откладывайте «Передать курьеру»: если площадка уже увезла заказ, а у нас он ещё «собран», программа спишет товар сама, но без документа «Отгрузка» и без строки в документах для 1С.',
    ],
  },

  // ================================================================ ГЛАВНАЯ
  {
    id: 'today',
    title: 'Главная',
    group: 'Начало работы',
    routes: ['/today'],
    roles: 'менеджер, администратор',
    summary: 'Экран «что требует внимания сейчас». Показываются только ненулевые пункты; щелчок по плитке открывает нужную очередь.',
    steps: [
      {
        title: 'Прочитать блок «Срочно»',
        text: '«Просрочен срок отгрузки», «Заказы без резерва» (не хватает товара или нет карточки), «Ошибки синхронизации» (обмен с WB / Ozon), «Недобор при сборке». Эти пункты разбирайте первыми.',
      },
      {
        title: 'Блок «Заказы FBS»',
        text: '«К сборке» — заказы в резерве без листа подбора. «Отгрузить в ближайшие 24 ч» с ближайшим сроком. «Ждут упаковки», «Собраны, ждут отгрузки», «Изменились на площадке», «Чужой товар в возвратах». «Отгружены, но площадка не приняла» — у нас отгружено больше 12 часов назад, а на площадке заказ всё ещё новый или на сборке: проверьте поставку WB или приёмку Ozon в кабинете.',
      },
      {
        title: 'Блок «Закупки и приёмка»',
        text: '«Пора заказать» (по продажам за 30 дней и запасу на 30 дней), «Ожидаемые поставки» (с подсказкой, сколько опаздывают), «Идёт приёмка» (товар ещё не в продаже), «Брак на проверке», «Неизвестные штрихкоды», «Черновики заказов поставщикам».',
      },
      {
        title: 'Блок «Остатки и инвентаризация»',
        text: '«Идёт инвентаризация», «Оприходование / списание ждут проведения», «Расхождения в остатках» (автоматическая сверка ячеек, резервов и зоны отгрузки).',
      },
      {
        title: 'Когда всё хорошо',
        text: 'Если все пункты по нулям, появится зелёная плашка «Всё спокойно».',
      },
    ],
    tips: [
      'Цифры обновляются сами каждые 30 секунд.',
      'Если показатели не загрузились, будет красная плашка «Не загрузилось» — это не ноль, обновите страницу.',
      'Цвет плитки: красный — срочно, оранжевый — требует решения, синий — для сведения.',
    ],
  },

  // ================================================================ ЗАКУПКИ
  {
    id: 'purchasing',
    title: 'Что заказать',
    group: 'Закупки',
    routes: ['/purchasing', '/analytics'],
    roles: 'менеджер, администратор',
    summary: 'Рекомендации, сколько заказать у поставщиков, по истории продаж. Расчёт открыт: видно каждое число. Отсюда создаются заказы поставщикам.',
    steps: [
      {
        title: 'Задать период и запас',
        text: 'В «Продажи за» выберите «7 дн», «14 дн», «30 дн», «60 дн» или «своё» (своё число дней). В «Запас после поставки, дней» — на сколько дней продаж должен хватить товар после прихода.',
      },
      {
        title: 'Отобрать товары',
        text: 'При необходимости выберите «Поставщик», введите «Поиск» (артикул или название). Переключатель «Только к заказу» оставляет только то, что нужно заказать.',
      },
      {
        title: 'Понять расчёт',
        text: 'Синяя плашка «Как считается» показывает формулу. Наведите мышь: на «Продано» — сколько продано отдельно и сколько в составе наборов; на «Доступно» — физический остаток, резерв, брак и товар на складах площадок; на «Рекомендовано» — полный расчёт с минимальной партией и кратностью.',
      },
      {
        title: 'Проверить предупреждения',
        text: 'Значок «⚠ N» в конце строки — наведите мышь: мало истории, 1–2 продажи за период, наборы с неподтверждённым составом, нет поставщика или срока поставки, товар уже в черновике заказа, есть брак, нет закупочной цены и т. п.',
      },
      {
        title: 'Поправить количество',
        text: 'Галочка слева — брать строку в заказ. В колонке «К заказу» можно изменить число. Если оно отличается от рекомендации, появится поле «Почему иначе?» — причина обязательна и сохранится в заказе.',
      },
      {
        title: 'Создать заказ',
        text: 'Внизу видно «Выбрано позиций», штук и сумму. Если у части товаров нет поставщика, выберите поставщика в списке рядом. Нажмите «Создать заказ поставщику». Создаётся по одному черновику на каждого поставщика; если он один — сразу откроется.',
      },
      {
        title: 'Excel',
        text: 'Кнопка «Скачать Excel» выгружает таблицу со всеми составляющими расчёта.',
      },
    ],
    tips: [
      'Продажи наборов раскладываются на составляющие по подтверждённому составу; один заказ считается один раз.',
      'Товар в незавершённой приёмке учитывается как остаток, брак — нет. Товар на складах WB и Ozon и в пути к ним вычитается из потребности.',
      'Если в системе продажи только за часть периода, появится жёлтая плашка «Мало истории продаж» — проверьте количество вручную.',
      'Условия поставщика (минимальная партия, кратность, срок поставки) задаются в «Справочники → Контрагенты», а для отдельного товара — в его карточке.',
      'Продажи со складов Ozon попадают в расчёт после загрузки кнопкой «Обновить остатки и продажи площадок» в «Поставки FBO / FBW».',
    ],
  },
  {
    id: 'purchase-orders',
    title: 'Заказы поставщикам',
    group: 'Закупки',
    routes: ['/purchase-orders'],
    roles: 'менеджер, администратор',
    summary: 'Список заказов поставщикам и карточка заказа. Статусы: «Черновик» → «Отправлен» → «Частично получен» → «Получен» или «Отменён».',
    steps: [
      {
        title: 'Список',
        text: 'Переключатель сверху: «Все», «Черновики», «Отправлены», «Частично», «Получены», «Отменены». Дата «Ожидается» красная, если поставка опаздывает. Щелчок по строке открывает заказ. Новый заказ делается кнопкой «Что заказать» (через рекомендации).',
      },
      {
        title: 'Проверить черновик',
        text: 'В карточке черновика в колонке «К заказу» можно поменять количество — сохраняется сразу при выходе из поля. Если число отличается от рекомендованного, программа спросит причину («Почему количество другое?»).',
      },
      {
        title: 'Отправить поставщику',
        text: '«Скачать Excel» — файл для поставщика. Когда заказ передан поставщику (почтой, звонком — вне программы), нажмите «Отправлен поставщику», укажите «Когда ожидается поставка» и нажмите «Отправлен». С этого момента товар считается «в пути» и появляется у склада в «Приёмка товара → Ожидаются».',
      },
      {
        title: 'Следить за приходом',
        text: 'Колонка «Получено» заполняется из приёмок. Оранжевое число — недопоставка, фиолетовое — привезли больше заказанного.',
      },
      {
        title: 'Закрыть с недопоставкой',
        text: 'Если поставщик довезти остаток не сможет, в частично полученном заказе нажмите «Закрыть с недопоставкой», впишите «Причина» и «Закрыть заказ». Недовезённое перестанет считаться «в пути».',
      },
      {
        title: 'Отменить',
        text: 'Черновик или отправленный заказ можно отменить кнопкой «Отменить».',
      },
    ],
    tips: [
      'Программа ничего не отправляет поставщику сама: «Отправлен поставщику» — это отметка для учёта.',
      'Нельзя отменить заказ, по которому уже идёт приёмка.',
      'Товар, привезённый сверх заказа, попадает в заказ строкой «не заказывали».',
    ],
  },

  // ================================================================ ПРИЁМКА
  {
    id: 'receipts',
    title: 'Приёмка товара',
    group: 'Приёмка',
    routes: ['/receipts', '/tsd/receipt'],
    roles: 'кладовщик, менеджер, администратор; решения по браку и неизвестным кодам — менеджер',
    summary: 'Приём товара от поставщика: по заказу поставщику, без заказа или по фото накладной. Пока приёмка не завершена, товар лежит на складе, но в продажу не идёт.',
    steps: [
        {title:'Скан накладной и сумма НДС',text:'Компактный ввод приёмки. На вкладке «Товары» штрихкод, камера, поиск номенклатуры и «Загрузить Excel» находятся в компактном блоке слева. На телефоне блок занимает доступную ширину. Таблица товаров по-прежнему использует ширину экрана; новые и отсканированные товары показываются сверху. Редактирование суммы НДС. В колонке «Сумма НДС» введите сумму из накладной поставщика и выйдите из поля или нажмите Enter. На телефоне поле есть в карточке товара. Два знака после запятой, запятая и точка допускаются; 0,00 — явный нулевой НДС. Пометка «Вручную» означает сохранённую сумму. Цена без НДС, количество и ставка при этом не меняются. Сумма «Всего» и итог приёмки учитывают введённый НДС, включая список приёмок и печатную накладную. Нажмите «К расчёту» или очистите поле, чтобы снова рассчитывать НДС по ставке. Ручная сумма остаётся фиксированной при последующем изменении количества, цены или ставки: проверьте её по документу поставщика. Если другой сотрудник уже изменил НДС, нажмите «Перечитать НДС» и сверьте актуальное значение. Изменение только НДС не обновляет закупочную цену карточки и не создаёт движений товара. Сохранение и просмотр скана накладной. Откройте приёмку → «Накладная» → выберите фото или PDF → «Сохранить скан накладной». Каждый файл показан отдельной рамкой в блоке «Загруженные накладные»: имя, размер, сотрудник и время. «Просмотреть» открывает PDF или фотографию поверх страницы без скачивания; «Открыть в отдельной вкладке» открывает просмотр в браузере. «Скачать скан» сохраняет оригинал. Файлы доступны с другого устройства с правами на тот же склад, в том числе после проведения приёмки. Одинаковый файл второй раз не добавляется. Удаление скана. В рамке нужного файла нажмите «Удалить». Проверьте имя, размер и время загрузки, затем нажмите «Удалить файл» или «Отмена». Удаляется только выбранное вложение; одноимённые файлы, товары и суммы приёмки сохраняются. Удаление записывается с сотрудником и временем и освобождает лимит вложений. В открытой на другом устройстве приёмке список обновляется; просмотр удалённого файла закрывается. Файл отменённой приёмки удалять нельзя. Допускаются PDF до 50 МБ, JPEG, PNG и WebP до 15 МБ на файл; для одной приёмки — до 10 файлов и 50 МБ суммарно. Прикрепление хранит оригинал поставщика, не распознаёт и не добавляет товары. Для ввода товаров по фото накладной остаётся прежний отдельный сценарий. Накладную программы можно скачать отдельной кнопкой «Скачать PDF накладной» рядом с «Печать»: сохраняется PDF с текущими товарами, суммами НДС и итогами, со штрихкодом номера и переносом длинной таблицы на страницы. «Скачать скан» возвращает оригинал поставщика, а «Скачать PDF накладной» — документ, сформированный программой.'},
      {
        title: 'Поиск и печать выбранных',
        text: 'Список приёмок. Слева единая кнопка «+ Приёмка» с крупным плюсом — новая приёмка без заказа. Колонка «Создана» стоит сразу после «Статус». Справа поле «Номер приёмки или поставщик»: введите часть номера или названия; неподходящие строки скрываются. Поиск не зависит от регистра. Очистите поле, чтобы снова увидеть список. Поиск действует на вкладках «Все приёмки» и «На приёмке». Печать выбранных. Отметьте приёмки флажками перед колонкой «Номер». Верхний флажок выбирает все видимые строки. После выбора появляется значок принтера. Наведите на него: подсказка «Печатать выбранные (N)» показывает количество. Нажмите значок: откроется одна печать, каждая накладная начинается с новой страницы. Без выбранных строк значок скрыт. За один раз можно выбрать до 100 приёмок. При смене поиска, вкладки или склада выбор очищается. Кнопка «Накладная» в строке сохраняет печать одного документа. Кладовщик печатает без денежных колонок.',
      },
      {
        title: 'Вкладки',
        text: 'Оформление вкладок. Названия вкладок складских разделов заключены в отдельные рамки с чуть более тёмным фоном; активная вкладка выделена синим. Порядок вкладок и действия сохранены. «Ожидаются» — отправленные заказы поставщикам (сколько позиций и штук осталось, дата, «опаздывает»). «На приёмке» — начатые, но не завершённые приёмки. «Все приёмки» — архив с кнопкой «Накладная» для печати. Внутри приёмки накладная — на отдельной вкладке «Накладная» (после «Товары»), там же кнопка «Печать». Брак и неизвестные штрихкоды разбираются в «Остатки → Оприходование и списание → Брак и проверка».',
      },
      {
        title: 'Начать приёмку по заказу',
        text: 'Во вкладке «Ожидаются» у нужного заказа нажмите «Начать приёмку». Если приёмка уже начата, кнопка называется «Продолжить приёмку». Откроется экран приёмки.',
      },
      {
        title: 'Приёмка без заказа',
        text: 'Кнопка «+ Приёмка» слева → выберите «Поставщик», впишите «Документ поставщика» (серия и номер ТТН) → «Начать приёмку».',
      },
      {
        title: 'Сканирование',
        text: 'На экране приёмки курсор стоит в поле «Штрихкод». Задайте «Кол-во» (сколько штук добавит один скан) и при желании «Ячейка» (пусто — без ячейки). Сканируйте товар сканером или нажмите «Камера» и наведите телефон на штрихкод. После каждого скана карточка показывает товар, «Принято сейчас», «По заказу» (сколько ещё ждём) и «Брак». Если товара нет в заказе, будет красная строка «он будет отмечен как лишний».',
      },
      {
        title: 'Товар без штрихкода',
        text: 'Поле «Нет штрихкода? Найти по артикулу или названию» — начните вводить (минимум 2 символа) и выберите товар из списка. Он будет принят в указанном количестве.',
      },
      {
        title: 'Брак',
        text: 'Перед сканом бракованной штуки включите «Брак / повреждение» и опишите, что с товаром (мятая упаковка, вскрыто…). Переключатель сам выключается после скана. Брак принимается на склад, но сразу удерживается и не продаётся.',
      },
      {
        title: 'Ошибся — отменить скан',
        text: 'Под карточкой последнего скана есть «Отменить этот скан (−N)». Отменяется только последний скан.',
      },
      {
        title: 'Неизвестный штрихкод',
        text: 'Если кода нет в номенклатуре, появится оранжевая плашка. Товар сам не создаётся. Отложите его отдельно, впишите, что это за товар (название с упаковки), и нажмите «Записать N шт как неизвестный товар». Если это просто ошибка сканирования — «Это ошибка сканирования».',
      },
      {
        title: 'Загрузка Excel',
        text: '«Загрузить Excel» → файл .xlsx или .csv с колонками «Артикул» или «Штрихкод», «Количество», по желанию «Ячейка» → «Проверить» (ничего не записывает, показывает, что будет принято и какие строки с ошибкой) → «Принять». Повторная загрузка того же файла приёмку не удваивает.',
      },
      {
        title: 'Проверить «Что пришло»',
        text: 'Таблица «Что пришло»: «Заказано», «Принято раньше», «Принято сейчас», «Брак», «Осталось» и отметка: «сходится», «недостача», «больше заказа», «не заказывали», «без заказа». Сверху — итоговые значки расхождений.',
      },
      {
        title: 'Завершить приёмку',
        text: 'Нажмите «Завершить приёмку». Если всё сходится, товар сразу становится доступным для продажи. Если есть недостача, лишнее, брак или неизвестные коды, откроется окно «Приёмка с расхождениями» со списком — проверьте и нажмите «Подтвердить с расхождениями» или «Вернуться к приёмке».',
      },
      {
        title: 'Приёмка по фото накладной',
        text: 'Кнопка «Файл накладной» → загрузите PDF, скан, фото, Excel или CSV → «Распознать». Проверьте каждую строку: «Товар в системе» (можно выбрать или нажать предложенный артикул), «Количество» (с пометкой «проверьте»). Укажите «Документ поставщика» и «Ячейка» → «Оприходовать».',
      },
      {
        title: 'Разбор брака (менеджер)',
        text: '«Остатки → Оприходование и списание → Брак и проверка», таблица «Брак и товар на проверке»: «В продажу» (товар годен), «Списать» (нужен номер акта о браке) или «Поставщику» (возврат поставщику, нужен номер акта или причина) → «Записать».',
      },
      {
        title: 'Разбор неизвестных штрихкодов (менеджер)',
        text: 'Там же, таблица «Неизвестные штрихкоды»: «Это товар…» → выберите карточку — штрихкод добавится в неё, количество будет оприходовано по той же приёмке. Или «Не наш — вернуть» → впишите причину → «Записать».',
      },
    ],
    tips: [
      'Пока приёмка не завершена, товар не входит в «Доступно», в наборы и в выгрузку.',
      'По Положению о приёмке товаров (пост. СМ РБ № 1290) при недостаче или браке поставщика уведомляют в течение 24 часов и составляют акт; если в договоре другие условия — действуют они. Акт оформляется вне программы.',
      'После подтверждения лишний товар идёт в продажу, а в заказе поставщику появляется строкой «не заказывали».',
      'Приёмка «Файл накладной» проводится сразу по нажатию «Оприходовать» — без отдельного подтверждения расхождений. Распознавание ошибается в цифрах, проверяйте количество.',
      'Камера работает на Android, на iPhone (Safari) и на компьютере с камерой — по адресу https.',
      'Товар от поставщика принимают только здесь, а не через «Оприходование».',
    ],
  },
  {
    id: 'customer-returns',
    title: 'Возвраты от покупателей',group:'Приёмка',routes:['/customer-returns'],
    roles:'кладовщик собирает позиции; менеджер подтверждает себестоимость и завершает приёмку',
    summary:'Возвраты разделяются по WB и Ozon. После завершения одной приёмки создаётся и проводится одно оприходование с себестоимостью на дату оформления заказа.',
    steps:[
      {title:'Завершение без цены и дополнение',text:'Оприходование без цены. Приёмку возвратов можно завершить, даже если себестоимость отдельных товаров не найдена. Пустая цена означает неизвестную себестоимость; 0,00 — известную нулевую цену. Текущая цена карточки вместо неизвестной цены возврата не подставляется. Дата оформления заказа остаётся обязательной: для товара без заказа менеджер указывает её и нажимает «Сохранить дату заказа», оставив цену пустой. В списке «Оприходование и списание» проведённый документ с хотя бы одной пустой ценой выделен оранжевым и отмечен «Требует дополнения». Количество уже поступило на склад; документ остаётся проведённым. Пока цены заполнены не полностью, общий денежный итог показан прочерком, в печатном акте также есть предупреждение. Откройте оприходование из возвратов. В оранжевом блоке «Требует дополнения» менеджер или администратор вводит недостающие цены без НДС, указывает «Источник недостающей цены» и нажимает «Дополнить цены». Для каждой строки показаны номер, количество, ячейка, дата заказа и выдачи возврата. Весовой товар — цена за кг. Можно дополнить часть строк: пометка останется до заполнения всех цен. При одновременном изменении другим сотрудником нажмите «Перечитать цены документа» и проверьте строки. После заполнения последней недостающей цены пометка снимается автоматически. Цена и источник записываются в историю изменений документа. Дополнение не создаёт повторного движения товара, не меняет количество, ячейку и даты, не заменяет уже известные цены. Обычное оприходование без цены тоже выделяется в списке; его цены можно заполнить существующей кнопкой «Сохранить изменения».'},
      {title:'Автоматическая себестоимость',text:'Автозаполнение себестоимости возвратов. При добавлении товара программа ищет цену в истории на момент оформления заказа покупателем. Уже добавленные строки без цены автоматически заполняются при открытии соответствующей незавершённой приёмки. Во время расчёта показано «Автоматически заполняется себестоимость…». Сохранённая вручную цена не заменяется. Согласованная оценка для сентября 2026 года. Если подтверждённой исторической цены нет, используется первое проведённое октябрьское оприходование этого товара на том же складе. Цена отмечена оранжевым «Оценка», в колонке «Источник цены» указан номер и дата документа. Это приблизительная оценка, а не подтверждённая себестоимость на дату заказа. Оценка автоматически сохраняется; повторное ручное подтверждение для завершения не требуется. Уточнить её можно по прежнему сценарию: ввести цену и источник, нажать «Подтвердить цену». Источник оценки выбирается только среди октябрьских оприходований 2026 года: отменённые, удалённые документы и оприходования из приёмок возвратов исключены. Если в первом документе нет цены, строка остаётся для ручного ввода. Более поздний документ и текущая цена карточки не подставляются вместо неё. Разные ячейки одного товара в первом документе учитываются по количеству; отсутствие цены хотя бы у одной строки не игнорируется. Для весового товара цена на экране — за килограмм. После завершения приёмки цена и пометка «Оценка» вместе с документом-источником сохраняются в строке созданного оприходования. Даты заказа и выдачи возврата не меняются. Автозаполнение не увеличивает остатки до завершения и не пересчитывает уже проведённые документы. Если дата заказа неизвестна, менеджер сохраняет её вручную. Подходящую цену можно внести позже в проведённом оприходовании. Правило оценки ограничено сентябрьскими заказами 2026 года.'},
      {title:'Один заказ — одна карточка',text:'Один заказ — одна карточка сканирования. После скана заказа или возвратного стикера весь найденный состав показывается внутри одной карточки «Заказ …». Сверху видны название набора, количество позиций и общее количество в соответствующих единицах. Например, один возврат набора из трёх разных супов показывает один заказ, три позиции по одному брикету. Одинаковые номера разных кабинетов и площадок не объединяются. Внутри карточки проверьте название, количество и состояние каждого товара. У фактически вернувшихся составляющих нажмите «Добавить в приёмку»; отсутствующие или лишние уберите кнопкой «Убрать найденное». При частичной приёмке оставшиеся товары продолжают отображаться в этой же карточке. Когда все составляющие подтверждены или убраны, карточка исчезает из очереди. Повторное сканирование не добавляет дубли уже найденных строк. Сканирование на телефоне автоматически обновляет компьютер, если на обоих устройствах открыты один склад, площадка и та же незавершённая приёмка. Обновлять страницу после каждого скана не нужно. Количество, принятие и удаление также синхронизируются. После установки новой версии обновите обе страницы один раз. Без связи дождитесь подключения и проверьте сохранённые строки. Разные единицы (килограммы, штуки, брикеты) не складываются в одну величину.'},
      {title:'Одна приёмка на телефоне и компьютере',text:'Совместная приёмка с телефона и компьютера. На обоих устройствах выберите один склад, WB или Ozon и одну запись в поле «Незавершённая приёмка». Сверьте короткий номер «Общая приёмка» под формой. Не начинайте две отдельные приёмки для одной работы. Все сотрудники с доступом к этому складу видят её сохранённые строки. После сканирования заказа или стикера найденные товары сохраняются на сервере и появляются на другом устройстве без обновления страницы, обычно в течение секунды при устойчивом соединении. Отображаются количество, состояние, примечание и сотрудник. На телефоне кнопки «Добавить в приёмку» и «Убрать найденное» находятся в карточке товара. Штрихкод самого товара по-прежнему сразу добавляет его в текущую приёмку. Количество в найденной строке сохраняется после выхода из поля. Добавление, удаление, подтверждённые цены и завершение приёмки также обновляются на другом устройстве. Если строка изменена одновременно, программа сохраняет ваш несохранённый ввод на экране и сообщает о конфликте; нажмите «Перечитать строку» или «Перечитать цену», проверьте актуальные данные и повторите изменение. При удалении строки другим сотрудником она исчезает из списка. После восстановления связи программа перечитывает общую приёмку. Изменения без ответа сервера не считаются сохранёнными: проверьте ошибку и повторите действие после подключения. Очередь старой версии переносится из этой вкладки автоматически после выбора соответствующей приёмки. Перенесённые строки отмечены: проверьте повторы. Если показана ошибка переноса, старые сканы остаются в этой вкладке; нажмите «Повторить перенос». Не закрывайте её до успешного переноса. Пока остаются найденные или неперенесённые строки, завершение заблокировано. Подтвердите фактически возвращённые товары и уберите лишние. Остатки меняются только один раз при завершении приёмки и проведении оприходования. Себестоимость и даты заказа/выдачи возврата сохраняются по прежним правилам.'},
      {title:'Выбрать площадку и начать приёмку',text:'«Маркетплейс возврата» → WB или Ozon → «Начать приёмку WB/Ozon». Незавершённую приёмку можно выбрать для продолжения. Смена площадки очищает выбранную приёмку и найденный заказ.'},
      {title:'Добавить товары',text:'Выберите «Годен», «Брак» или «Чужой товар», количество и ячейку. Сканируйте номер заказа и принимайте позиции из его состава либо сразу сканируйте товар. После «Добавить в приёмку» товар появляется в текущей приёмке со счётчиком позиций и полями себестоимости; остатки до её завершения не увеличиваются. При сканировании штрихкода самого товара он добавляется сразу. Ошибочную позицию можно убрать.'},
      {title:'Распознать этикетку Ozon FBS',text:'Выберите Ozon и отсканируйте QR этикетки. Код вроде 801952456514000 отличается от номера отправления: программа сверяет его с кабинетом Ozon и показывает товар, даже если старого заказа нет в WMS. Для набора показываются составляющие и количество по возврату; проверьте фактически полученное и нажмите «Добавить в приёмку». Дата оформления и дата выдачи продавцу сохраняются в оприходовании. Цена продажи покупателю не используется вместо себестоимости. При отсутствии истории менеджер подтверждает цену по 1С. Повтор и превышение количества возврата блокируются; неоднозначный или неизвестный код не принимается. Поддерживается один настроенный кабинет Ozon; FBO-этикетки этим сценарием не проверены.'},
      {title:'Распознать возвратный стикер WB',text:'Выберите WB и нажмите «Камера» либо сканируйте стикер обычным сканером. QR и Code128 с кодом вида *DQ2nCHmV, а также напечатанный номер стикера сверяются с отчётом возвратов WB за последние 31 день (право подключения «Аналитика»). Показываются заказ, статус и товар, даже если заказа нет в WMS. Найденные позиции сохраняются в таблице «Отсканированные товары — проверьте перед добавлением», новые сверху. Проверьте состав и нажмите «Добавить в приёмку» у фактически возвращённых товаров; лишние строки удалите кнопкой «Убрать найденное». Смена скана не скрывает предыдущие товары. Очередь сохраняется при обновлении той же вкладки; чтобы продолжить, выберите площадку и незавершённую приёмку. Набор принимается составляющими. Повторное превышение количества по одному стикеру блокируется. При отсутствии или неоднозначной связи товар автоматически не принимается.'},
      {title:'Звук, вибрация и даты документа',text:'Успешное распознавание и добавление подтверждаются коротким высоким сигналом и вибрацией; ошибка — низким сигналом и двойной вибрацией. Для камеры нужен HTTPS и разрешение браузера. Звук и вибро зависят от устройства. В оприходовании и печатном акте есть «Дата заказа» и «Возврат выдан». Дата выдачи берётся из отчёта WB; при отсутствии данных остаётся пустой, сегодняшней датой не заменяется.'},
      {title:'Проверить себестоимость на дату продажи',text:'Для цены используется дата оформления заказа. Если есть достоверная история закупочной цены на этот момент, она подставится автоматически. История начинается с момента установки обновления; начальный снимок не выдаётся за цену старой продажи. Без истории или без номера заказа менеджер указывает дату, себестоимость без НДС и источник (например1С), затем «Подтвердить цену». Весовой товар — цена за кг, два знака после запятой. Текущая цена карточки вместо исторической не подставляется.'},
      {title:'Завершить и создать оприходование',text:'Проверьте позиции и нажмите «Завершить приёмку возвратов», затем «Да». Создаётся одно проведённое оприходование по этой площадке; остаток увеличивается ровно один раз. Брак остаётся удержан. Чужой товар в складские строки не включается; если в приёмке только чужой товар, складское оприходование не создаётся. Повтор завершения возвращает тот же документ.'},
      {title:'Открыть документ и список',text:'Нажмите «Открыть оприходование» либо «Оприходование» в строке возврата. Разные даты и цены одного товара сохранены отдельными строками. Документ связан с приёмкой: можно изменить причину, состав/даты/основание и проведение защищены. Фильтры «Все / WB / Ozon / Не указана» разделяют принятые возвраты. «Не указана» — старые записи без подтверждённой площадки. Уже принятые старые возвраты повторно не оприходуются.'},
      {title:'Чужой товар',text:'После завершения приёмки менеджер может нажать «Закрыть» у чужого товара, указать результат претензии/возврата площадке и нажать «OK».'},
    ],
    tips:['Наборы возвращаются составляющими — принимается физически полученный товар. Перед завершением добавьте или уберите все найденные строки, подтвердите даты и цены; причина блокировки показана рядом с кнопкой.','Изменившуюся в другом окне приёмку необходимо перечитать и проверить перед завершением.','При сканировании стикера статус читается из отчёта WB/Ozon; фактическую приёмку подтверждает сотрудник.'],
  },

  // ================================================================ ОСТАТКИ
  {
    id: 'stocks',
    title: 'Остатки',
    group: 'Остатки',
    routes: ['/stocks'],
    roles: 'все; ручная корректировка и загрузка из Excel — менеджер, администратор',
    summary: 'Сколько товара на складе: физически, в резерве, не продаётся, доступно и сколько уйдёт на площадки. По щелчку — ячейки и история движений.',
    steps: [
      {
        title: 'Найти товар',
        text: 'Введите артикул, название или штрихкод в поле поиска справа вверху страницы (или в общем поиске в шапке). Фильтр категорий выбирает группу товаров. «Заголовки категорий» группирует строки по категориям (щелчок по заголовку сворачивает группу). «Только проблемы» оставляет проблемные позиции.',
      },
      {
        title: 'Понять колонки',
        text: '«Физический» — лежит на складе. «Резерв FBS» — под заказы. «Не продаётся» — брак, незавершённая приёмка, возврат из зоны отгрузки (наведите мышь — расшифровка). «Доступно» = физический − резерв − не продаётся. «Буфер» — сколько штук не показываем на площадках. «К публикации FBS» = доступно − буфер. «Опубликовано» — последнее число, отправленное на площадки.',
      },
      {
        title: 'Окно товара: остатки, цена, история',
        text: 'Щёлкните по строке — справа откроется окно товара. «Остатки»: остаток, резерв, доступно к заказу и «По ячейкам» (хранение, зона отгрузки, без адреса). Для менеджера ещё: «Цена» — закупочная цена из карточки, стоимость остатка и цены в приёмках; «История» — приёмки, оприходования и списания по товару (переключатель «Все / Приёмки / Оприходования / Списания»); «Все движения» — каждое движение по ячейкам, кто и по какому основанию. Отменённые и удалённые документы там не показываются.',
      },
      {
        title: 'Изменить остаток',
        text: 'Вручную остаток не правится — только документом «Остатки → Оприходование и списание» (с причиной и актом).',
      },
      {
        title: 'Остатки из Excel (менеджер)',
        text: 'Кнопка «Остатки из Excel» → «Скачать шаблон» при необходимости → выбрать файл → «Проверить» (показывает, сколько позиций изменится, что добавится и спишется, ошибки) → «Загрузить».',
      },
    ],
    tips: [
      'Загрузка из Excel ставит остаток в каждой указанной ячейке равным файлу — как инвентаризация. Весовые товары указываются в килограммах. Наборы не вносятся — только их составляющие.',
      'Для обычных исправлений лучше использовать документ «Оприходование и списание»: он проводится менеджером и печатается актом.',
      'Комплект физически не хранится: его остаток считается из составляющих.',
      'Красное «Доступно» (меньше нуля) — продано больше, чем есть: проверьте резервы и приёмки.',
      'Бейдж «на МП 0» у категории — есть свободный товар, а на площадку уходит 0 (например, из-за буфера).',
    ],
  },
  {
    id: 'product-flow',
    title: 'Движение товара',
    group: 'Остатки',
    routes: ['/product-flow'],
    roles: 'все сотрудники',
    summary: 'Вся история одного товара на складе: когда и сколько пришло, куда ушло — по каждому артикулу, в котором он продаётся (сам и в наборах), списания, инвентаризации, остаток по дням. Весовой товар показывается в граммах и килограммах.',
    steps: [
      { title: 'Выбрать товар', text: '«Остатки → Движение товара». В поле «Товар» начните вводить артикул, название или штрихкод, например «Вишенка», и выберите товар из списка. Наборы в списке не показываются: они не лежат на складе, их расход виден у составляющих. Из «Остатков» сюда же ведёт кнопка «Движение товара →» в карточке товара.' },
      { title: 'Выбрать период', text: 'Переключатель «7 дн / 30 дн / 90 дн / год». Все цифры и графики считаются за выбранный период.' },
      { title: 'Где продаётся', text: 'Над цифрами — список артикулов с этим товаром: сам товар и наборы с количеством в одном наборе, например «Вишенка 500 г · 500 г в 1 шт», «Ассорти 200 г · 100 г в 1 шт».' },
      { title: 'Итоги', text: 'Плитки: на начало периода, пришло, ушло по заказам FBS, отправлено на FBO, списано и оприходовано, результат инвентаризаций, остаток сейчас, в резерве и свободно.' },
      { title: 'Графики', text: '«Остаток на складе по дням» — как менялся остаток; «Приход и расход по дням» — синие столбики вверх (пришло), оранжевые вниз (ушло). Наведите мышь на день — появится подсказка с цифрами.' },
      { title: 'Куда ушёл товар', text: 'Таблица по артикулам: сколько граммов или штук ушло в каждый артикул, сколько ещё в резерве (заказы собираются), доля и число заказов. Например: «Вишенка 500 г — 12,5 кг», «Ассорти 200 г — 2,4 кг».' },
      { title: 'События', text: 'Внизу — каждое событие с документом: приёмка (номер, поставщик), заказ WB/Ozon (номер и артикул), поставка FBO, документ списания или оприходования, инвентаризация; сколько и какой остаток стал после. Самое свежее — сверху. Переключатель «Показать перемещения внутри склада» добавляет перекладку по ячейкам и в зону отгрузки — остаток она не меняет.' },
    ],
    tips: [
      'Расход по наборам считается по составу: 1 набор «Вишенка 1000 г» = 1000 г «Вишенки». Проверьте состав наборов в «Справочники → Товары и комплекты».',
      'Продажи со складов WB и Ozon (FBO) здесь не видны — только то, что уехало с нашего склада, в том числе поставкой на FBO.',
    ],
  },
  {
    id: 'inventory',
    title: 'Инвентаризация',
    group: 'Остатки',
    routes: ['/inventory'],
    roles: 'назначение, проверка и утверждение — менеджер, администратор; подсчёт — кладовщик',
    summary: 'Пересчёт товара по приказу с комиссией (по Инструкции Минфина РБ № 180): снимок учёта → подсчёт → проверка → повторный пересчёт → утверждение. Склад во время пересчёта продолжает работать. Вкладка «Сверка остатков» — автоматическая проверка учёта.',
    steps: [
      {
        title: 'Назначение',
        text: 'Вкладка «Инвентаризации» → «Назначить инвентаризацию». Заполните «Основание» (Приказ № … от …), «Вид» (плановая, перед годовой отчётностью, смена материально ответственного лица, выборочная, после порчи или хищения) и объём: «Весь склад», «Зона» или «Ячейки» (адрес ячейки и Enter).',
      },
      {
        title: 'Назначение: комиссия',
        text: 'Для каждого члена комиссии выберите роль («Председатель», «Член комиссии», «МОЛ»), должность и Фамилию И.О.; «+ член комиссии» добавляет строку. Состав подставляется из прошлой инвентаризации. Если в комиссии меньше двух человек или нет МОЛ, появится предупреждение.',
      },
      {
        title: 'Назначение: вслепую и старт',
        text: 'Переключатель «Вслепую» (рекомендуется) скрывает от кладовщика учётное количество. Нажмите «Начать» — программа запомнит учётные остатки выбранных ячеек и откроет документ. Статус — «Идёт подсчёт».',
      },
      {
        title: 'Подсчёт',
        text: 'Откройте инвентаризацию из списка (кнопка «Открыть»). Выберите «Ячейка» из списка или введите адрес в «или другая». Задайте «Шт за скан». Сканируйте товар в поле «Сканируйте товар в …» или кнопкой «Камера» — каждый скан прибавляет штуки. Зелёная плашка показывает, сколько посчитано по строке.',
      },
      {
        title: 'Подсчёт: исправление',
        text: 'Если насчитали неправильно, в таблице у строки нажмите «Изменить», введите «Фактически, шт» → «Сохранить». Товар, которого нет в учёте ячейки, добавляется строкой-излишком с пометкой «не было в учёте».',
      },
      {
        title: 'Проверка (менеджер)',
        text: 'Когда всё посчитано, нажмите «Закончить подсчёт» — статус «На проверке». Сверху видны «Посчитано строк», «На пересчёте», «Недостача» и «Излишек» (штуки и сумма по закупочной цене). Фильтры таблицы: «Все», «Не посчитано», «Расхождения», «Пересчёт». Вернуть документ в подсчёт — «Вернуть в подсчёт».',
      },
      {
        title: 'Повторный пересчёт',
        text: 'Отметьте галочками спорные строки и нажмите «Повторный пересчёт (N)». Строки вернутся на подсчёт, первый результат и кто его насчитал сохранятся («первый раз N»). Пока пересчёт не закончен, утвердить нельзя.',
      },
      {
        title: 'Утверждение',
        text: 'Нажмите «Утвердить» и подтвердите. Остатки исправляются на разницу движением «Инвентаризация: недостача / излишек». Если есть непосчитанные строки, программа отдельно спросит, утверждать ли без них (они не меняются).',
      },
      {
        title: 'Печать',
        text: '«Инвентаризационная опись» — факт и учёт, количество и сумма, расписки МОЛ, подписи комиссии. «Сличительная ведомость» — только расхождения с суммами и отметкой о пересчёте.',
      },
      {
        title: 'Отмена',
        text: 'До утверждения инвентаризацию можно отменить: «Отменить» → «Причина» → «Отменить без изменения остатков».',
      },
      {
        title: 'Сверка остатков',
        text: 'Вкладка «Сверка остатков» → «Проверить сейчас». Проверяется: ячейки сходятся с итогами, резервы — с заказами и поставками, продано не больше, чем есть, в зоне отгрузки только собранные заказы. Если всё хорошо — зелёная плашка, иначе таблица «Проверка / Товар / Что не так».',
      },
    ],
    tips: [
      'Разница считается от учёта на момент подсчёта строки, поэтому приёмка и отгрузка во время инвентаризации не дают ложных недостач.',
      'Зона отгрузки не пересчитывается — там лежат собранные заказы.',
      'Две инвентаризации на одни и те же ячейки одновременно не запускаются.',
      'Утверждённую инвентаризацию отменить нельзя — ошибку исправляют документом «Оприходование и списание».',
      'Бухгалтерские проводки и отнесение недостач делаются в 1С по распечатанным описи и ведомости.',
      'На ТСД плитка «Инвентаризация» открывает этот же экран — считать можно с телефона.',
    ],
  },
  {
    id: 'stock-docs',
    title: 'Оприходование и списание',
    group: 'Остатки',
    routes: ['/stock-docs'],
    roles: 'черновик — все; провести и отменить — менеджер, администратор',
    summary: 'Любая правка остатка вне приёмки, отгрузки и инвентаризации оформляется документом с причиной. Кладовщик готовит черновик, менеджер проводит.',
    steps: [
      { title: 'Причина возвратного оприходования', text: 'Причина возвратного оприходования. Менеджер или администратор своего склада может изменить причину уже проведённого документа WB/Ozon. Откройте документ → «Изменить» рядом с причиной → введите от 3 до 300 символов → «Сохранить причину» либо «Отмена». Сохранённая причина видна в карточке, списке и печатном акте. Во вкладке «История изменений» раскройте запись «Изменён документ»: видны сотрудник, время и значения «Было» / «Стало». Повторное сохранение той же причины не создаёт пустую запись. Состав, количество, цены, даты заказа/выдачи возврата, основание, связь с WB/Ozon и проведение при изменении причины сохраняются. Для отменённого документа и кладовщика изменение причины проведённого возврата недоступно.' },
      {
        title: 'История изменений',
        text: 'Откройте документ и выберите вкладку «История изменений». Здесь видны дата и время по Минску, сотрудник и действие. Раскройте запись, чтобы увидеть товар, поле и значения «Было» / «Стало». «Раньше» и «Новее» переключают страницы. В старых записях подробности могут отсутствовать: прежний журнал не сохранял конкретные значения. Во вкладке «Документ» остаются обычные действия; история показывает только сохранённые изменения.',
      },
      {
        title: 'Создать документ',
        text: 'Оприходования и списания — на разных вкладках: «Оприходование» и «Списание». На нужной вкладке нажмите «Оприходовать» (добавить товар) или «Списать» (убрать товар).',
      },
      {
        title: 'Причина',
        text: '«Причина» обязательна: выберите готовую кнопку (например «порча / брак на складе», «излишек, найденный вне инвентаризации») или впишите свою. Документ открывается на весь экран.',
      },
      {
        title: 'Добавить товары',
        text: 'Отсканируйте или введите «Штрихкод или артикул» и нажмите Enter, или нажмите «Камера» — на телефоне можно сканировать подряд, окно не закрывается до «Готово». Каждый скан — +1 шт. Количество и «Ячейка» правятся прямо в строке, ✕ удаляет строку. В оприходовании доступны цена без НДС, ставка НДС и денежные итоги. Ячейка подставляется автоматически, но её нужно проверить перед проведением.',
      },
      {
        title: 'Загрузить Excel в оприходование',
        text: 'Нажмите «Загрузить Excel», выберите .xlsx или .csv с колонками «Артикул» и «Количество» и нажмите «Проверить». Для весовых товаров укажите «Ед. изм.»: г или кг. Наборы в файле должны быть разложены на составляющие. Исправьте ошибки и нажмите «Загрузить в документ»: текущий список товаров будет заменён. Цены и номера заказов из файла не учитываются. Остатки увеличатся только после «Провести».',
      },
      {
        title: 'Сохранить или провести',
        text: '«Сохранить черновик» — документ ждёт менеджера (на «Главной» появится «Оприходование / списание ждут проведения»). Менеджер может сразу нажать «Провести».',
      },
      {
        title: 'Провести черновик (менеджер)',
        text: 'В списке (фильтр «Черновики») щёлкните по документу → «Провести». Остатки меняются сразу, программа возвращается к списку. Фильтр «Все» показывает черновики и проведённые; отменённые — только по фильтру «Отменённые».',
      },
      {
        title: 'Печать акта',
        text: 'Кнопка «Акт» печатает документ с подписями. В акте оприходования — товары, количества и ячейки; в акте списания также показываются цены и суммы.',
      },
      {
        title: 'Исправить ошибку',
        text: 'Откройте проведённый документ → «Исправить»: документ возвращается в черновик под тем же номером (остатки откатываются). Поправьте строки и проведите снова. Без «Исправить» можно менять причину и примечания, а в списании также цены и НДС. «Отменить» → «Причина отмены» → «Отменить документ» — остатки вернутся, документ уйдёт в «Отменённые». Красная корзина в списке удаляет документ: черновик — просто удаляется, проведённый — сначала откатывается.',
      },
    ],
    tips: [
      'Списать больше, чем лежит в ячейке, нельзя.',
      'Отмена не пройдёт, если остаток ушёл бы в минус — например, оприходованный товар уже продан.',
      'Набор сам раскладывается на составляющие (например, возврат набора с маркетплейса). Набор без состава или с составом «на проверке» не добавится.',
      'Товар от поставщика оформляйте приёмкой по заказу, а не оприходованием.',
    ],
  },

  // ================================================================ ЗАКАЗЫ FBS
  {
    id: 'orders',
    title: 'Заказы FBS',
    group: 'Заказы FBS',
    routes: ['/orders'],
    roles: 'менеджер, администратор',
    summary: 'Заказы WB и Ozon, которые собираем со своего склада. Вкладки совпадают с кабинетом площадки, а отдельной отметкой видно, что с заказом у нас. Отсюда загружаются заказы и создаются листы подбора.',
    steps: [
      {
        title: 'Загрузить заказы',
        text: 'Заказы обновляются автоматически: сервер раз в минуту читает WB и Ozon (в кабинеты ничего не отправляется), новые заказы сразу резервируют товар и появляются на экране у всех без перезагрузки. Под кнопкой видно «авто каждые 60 с · обновлено …»; если площадка не ответила — оранжевая надпись, программа повторит сама. Чтобы не ждать, нажмите «Обновить сейчас». Пока идёт загрузка, кнопка крутится; в конце всплывает итог по каждой площадке: сколько новых, сколько закрыто уехавших. Если загрузку уже запустил коллега, программа скажет «Загрузка уже идёт».',
      },
      {
        title: 'Выбрать площадку и вкладку',
        text: 'Переключатель «Wildberries» / «Ozon». Вкладки WB: «Проблемы», «Новые», «На сборке», «В доставке», «Завершённые», «Отменённые». Вкладки Ozon: «Проблемы», «Ожидают сборки», «Ожидают отгрузки», «Доставляются», «Доставлены», «Отменены», «Все». Рядом с вкладкой — число заказов. Строка над вкладками показывает, когда последний раз сверяли с кабинетом.',
      },
      {
        title: 'Прочитать строку заказа',
        text: '«Заказ» — номер (кнопка ⧉ копирует его для поиска в кабинете), когда принят, статус на площадке. «Состав» — товары с фото и категорией. «Отгрузить до» — срок (оранжевый — меньше 6 часов, красный — просрочен), у Ozon — тариф, у WB — время автоотмены. «Статус»: «у нас» (не зарезервирован, товар в резерве, в подборе, собран, отгружен) и «на площадке», плюс подсказка «→ в кабинете …», что сделать руками.',
      },
      {
        title: 'Разобрать проблемы',
        text: 'Вкладка «Проблемы». Под составом красным «⚠ …» написано, что не так. «товар не сопоставлен — открыть маппинг» ведёт в связи с WB / Ozon. Если товар появился, у заказа без резерва нажмите «Зарезервировать».',
      },
      {
        title: 'Отобрать заказы',
        text: '«Все кабинеты» — выбор кабинета, фильтр категорий (на вкладках проблем, новых и сборки), «Номер заказа» — поиск. Под фильтрами видно «Можно выбрать: N · уже в листе подбора · без резерва».',
      },
      {
        title: 'Собрать волну',
        text: '«Собрать волну» → выберите срок: «до 12:00 сегодня», «до 18:00 сегодня», «до конца дня», «до 12:00 завтра» или «Все готовые». Программа создаст листы подбора на заказы в резерве с таким сроком (с учётом выбранного кабинета и категорий) и откроет «Листы подбора».',
      },
      {
        title: 'Лист из выбранных',
        text: 'Отметьте заказы галочками (галочка в заголовке — все, что можно) и нажмите «Лист подбора из выбранных (N)». Во вкладке «На сборке» заказы разложены по папкам категорий, у папки есть «Лист подбора на папку (N)».',
      },
      {
        title: 'Сроки WB',
        text: 'Кнопка «Сроки WB» (на вкладке Wildberries): WB не передаёт срок в API, он считается от времени задания. Укажите для каждого кабинета «Отгрузить без штрафа, ч» и «Автоотмена через, ч» → «Сохранить». Сроки уже загруженных заданий пересчитаются. Сверьте часы с офертой в кабинете WB (сейчас 48 и 192).',
      },
      {
        title: 'Отменить заказ',
        text: 'Кнопка «Отменить» в строке снимает резерв у нас. Если заказ уже был собран, товар сразу возвращается в продажу — в ту ячейку, откуда его собрали; кладовщику остаётся забрать пакет из зоны отгрузки и положить товар на место. Так же программа поступает сама, когда заказ отменила площадка. Отмену на самой площадке делайте в её кабинете.',
      },
    ],
    tips: [
      'Весь путь заказа по шагам — в разделах «Заказ FBS Wildberries: от поступления до отгрузки» и «Заказ FBS Ozon: от поступления до отгрузки».',
      'Только в кабинете WB: добавить задания в поставку, передать поставку в доставку, отменить задание.',
      'Только в кабинете Ozon: нажать «Собрать» (после этого появится этикетка), сформировать отгрузку и акт, отменить отправление.',
      'Многоштучные отправления делятся по штукам — в строке будет «часть N из M».',
      'Если заказы ждут на другом складе, появится синяя плашка с кнопкой «Перейти на …».',
      'Страница листа подбора (печать листа, «Этикетки (N)», «Собрано по листу») — «Листы подбора» → «Открыть»; после создания одного листа она открывается сама.',
      'Если в итоге загрузки по Ozon есть «⚠ прочитано старым методом v3 — сообщите разработчику», передайте текст администратору.',
    ],
  },
  {
    id: 'fbs-wb',
    title: 'Заказ FBS Wildberries: от поступления до отгрузки',
    group: 'Заказы FBS',
    routes: [],
    roles: 'менеджер, комплектовщик, администратор',
    summary: 'Полный путь сборочного задания WB по шагам — так, как склад работает сейчас: менеджер за компьютером делает и печатает лист подбора и стикеры, комплектовщик собирает по бумажному листу, менеджер отмечает сборку, упаковку и передачу курьеру. Плюс что обязательно сделать руками в кабинете WB.',
    steps: [
      {
        title: '1. Задание пришло само',
        text: 'Ничего нажимать не нужно: сервер каждую минуту, круглосуточно читает кабинет WB. Новое сборочное задание сразу резервирует товар и появляется у всех на экране без перезагрузки. Где смотреть: «Заказы FBS → Заказы», переключатель «Wildberries», вкладка «Новые». В колонке «Статус»: «у нас: товар в резерве» и «на площадке: новое». Не хотите ждать минуту — «Обновить сейчас».',
      },
      {
        title: '2. Проверить вкладку «Проблемы»',
        text: 'Сюда попадают задания, которые нельзя собирать: «нет карточки в номенклатуре» (ссылка «открыть маппинг» ведёт в «Справочники → Связи с WB / Ozon»), «не хватает товара — резерв не встал», «состав набора не подтверждён», «просрочен срок отгрузки». Исправили причину — у задания нажмите «Зарезервировать». Задание без резерва в лист подбора не попадёт.',
      },
      {
        title: '3. Срок и автоотмена',
        text: 'Колонка «Отгрузить до»: оранжевый — осталось меньше 6 часов, красный — просрочено. Ниже — «автоотмена …»: к этому времени WB сам отменит задание, если его не передали в доставку. WB не передаёт срок, программа считает его от времени задания по часам из кнопки «Сроки WB» (вверху страницы, на вкладке Wildberries): «Отгрузить без штрафа, ч» и «Автоотмена через, ч» → «Сохранить». Сверьте часы с офертой WB.',
      },
      {
        title: '4. В кабинете WB: добавить задания в поставку',
        text: 'Делается только в личном кабинете WB — программа в WB ничего не пишет. «Сборочные задания» → создайте поставку (или откройте открытую) → добавьте в неё задания, которые будете собирать. Это нужно сделать до печати стикеров: WB даёт стикер только на задание в поставке. Через минуту задание у нас переедет во вкладку «На сборке» (на площадке: «на сборке»).',
      },
      {
        title: '5. Выбрать задания и создать лист подбора',
        text: 'На странице «Заказы» отметьте задания галочками (галочка в заголовке — все, что можно) → «Лист подбора из выбранных (N)». Во вкладке «На сборке» задания разложены по папкам категорий — у папки кнопка «Лист подбора на папку (N)». Или «Собрать волну» → «до 12:00 сегодня», «до 18:00 сегодня», «до конца дня», «до 12:00 завтра» или «Все готовые». Программа откроет «Листы подбора», у заданий статус «в подборе · номер листа».',
      },
      {
        title: '6. Открыть лист',
        text: '«Заказы FBS → Мои задания» (переключатель «Все») → щёлкните карточку «подбор» с номером листа. Откроется страница листа: прогресс «N из M строк собрано» и карточки строк — товар, заказ, ячейки, сколько взять.',
      },
      {
        title: '7. Распечатать лист подбора',
        text: 'На странице листа кнопка «Печать» (она же есть в «Листы подбора» в строке листа). На бумаге: номер листа со штрихкодом, дата, склад, число заказов и артикулов; дальше блоки по артикулам — сверху самые массовые, — в каждом блоке номера заданий с квадратиком для отметки, количество и ячейки. «нет в ячейках» — товара нет в ячейках хранения, разберитесь до сборки.',
      },
      {
        title: '8. Распечатать стикеры WB',
        text: 'На странице листа кнопка «Этикетки (N)» — стикеры на все задания листа. Программа запрашивает у WB готовые стикеры (в WB ничего не меняется) и открывает их в новой вкладке — окно печати откроется само. Формат — 58×40 мм, один стикер на страницу, под стикером номер задания и баркод: печатайте на термопринтере с лентой 58×40. Если стикеры пришли не на все задания — оранжевое сообщение «Этикеток получено: N». Обычно причина — задание не добавлено в поставку (шаг 4), уже отгружено или отменено.',
      },
      {
        title: '9. Отдать комплектовщику',
        text: 'Отдайте комплектовщику распечатанный лист и пачку стикеров. Он идёт по блокам листа: берёт товар из указанной ячейки, раскладывает по пакетам — один пакет на задание, — клеит на пакет стикер с номером этого задания (номер под стикером = номер на листе) и ставит отметку в квадратике. Собранные пакеты несёт в зону отгрузки. Если товара не хватило — пишет на листе, чего и сколько.',
      },
      {
        title: '10. Отметить лист собранным',
        text: 'Комплектовщик вернул лист. Менеджер открывает этот лист (шаг 6) → зелёная кнопка «Собрано по листу» → окно «Собрано по бумажному листу» со списком всего, что осталось собрать → сверьте с отметками на бумаге → «Всё собрано». Все задания листа становятся «собран», товар по учёту переходит из ячеек в зону отгрузки. Поле «Причина» заполнять нужно, только если в настройках включена сборка со сканом.',
      },
      {
        title: '11. Если что-то не нашли',
        text: 'Тогда «Всё собрано» не нажимайте. Щёлкайте по строкам листа по одной: для найденного проверьте «Ячейка» (программа проверит, что товар там есть) и «Количество» → «Подтвердить строку». Для ненайденного — «Не нашли всё количество — недобор» → «Сколько по факту нашли», «Причина недобора» → «Закрыть недобором». Задание становится «собран», когда подтверждена его последняя строка. Недобор виден на «Главной» — менеджер решает, что делать с заданием (например, отменить в кабинете WB).',
      },
      {
        title: '12. Упаковка',
        text: '«Заказы FBS → Упаковка» → «Wildberries» → «Ждут упаковки». Здесь все собранные задания по сроку. Колонка «Короб» подсказывает упаковку: коробка из состава набора, «заводская коробка» — отдельная не нужна, «не задан» — правило не настроено. Потерялся стикер — «Этикетка» в строке задания напечатает его ещё раз. Отметьте упакованные → «Упаковано (N)» (или «Упаковано» в строке). Это отметка для порядка, остаток она не меняет.',
      },
      {
        title: '13. Передать курьеру',
        text: 'Когда груз забирают (или везёте его на склад / ПВЗ WB): на странице «Упаковка» отметьте отдаваемые задания (они могут быть и в «Ждут упаковки», и в «Упакованы») → «Передать курьеру (N)» → подтвердите. Товар списывается со склада, появляется документ в «Заказы FBS → Отгрузки», задание «у нас: отгружен». Отгрузить можно только собранные задания.',
      },
      {
        title: '14. В кабинете WB: передать поставку в доставку',
        text: 'Сразу после «Передать курьеру» в кабинете WB передайте поставку в доставку и распечатайте там же QR-код (штрихкод) поставки — его клеят на короб / мешок поставки. Программа этого не делает и QR поставки не печатает. Пока WB не увидел передачу, у задания подсказка «→ в кабинете WB: передайте поставку в доставку», а через 12 часов оно появится на «Главной» в «Отгружены, но площадка не приняла».',
      },
      {
        title: '15. Накладная (ТТН)',
        text: '«Заказы FBS → Накладные» → «Создать накладную» → в поле «Отгрузка» выберите отгрузку из шага 13 — места и масса посчитаются сами → проверьте «Серия», «Номер», «Грузополучатель», «Пункт разгрузки», «Автомобиль», выберите водителя и подписантов → «Создать и распечатать». Если нужен бланк WB: скачайте PDF в кабинете WB → «Загрузить из кабинета» → «Wildberries» → «Загрузить» → в строке «Внести информацию» → «Сохранить» → «Печать заполненной».',
      },
      {
        title: '16. Следить за доставкой',
        text: 'Статусы WB программа читает сама: задание переходит во вкладку «В доставке», потом «Завершённые» (получено покупателем) или «Отменённые». В «Отгрузках» менеджер скачивает «Документы для 1С» по отгрузке или «Документы для 1С за период».',
      },
      {
        title: '17. Пункт приёма не взял часть',
        text: '«Заказы FBS → Отгрузки» → откройте отгрузку → отметьте непринятые задания → «Причина» (по умолчанию «Акт расхождения ПВЗ») → «Не принято ПВЗ (N)». Задания снова «собран», товар — в зоне отгрузки, резерв сохранён. Отгрузите их потом новой отгрузкой (шаг 13).',
      },
      {
        title: '18. Если задание отменили',
        text: 'Отмена в кабинете WB (покупателем, WB или автоотменой) приходит сама в течение минуты; менеджер может снять резерв у нас кнопкой «Отменить» в строке (отменить на WB — только в кабинете). Не собрано — резерв просто снимается. Уже собрано — товар по учёту сразу возвращается в продажу, в ту ячейку, откуда его взяли. Комплектовщику: найти пакет в зоне отгрузки, снять стикер и положить товар обратно в ячейку. Если WB отменил задание, пока оно в листе подбора, программа дождётся отметки сборки и вернёт товар так же автоматически. Подробнее — раздел «Отмена собранного заказа».',
      },
    ],
    tips: [
      'Только в кабинете WB: создать поставку и добавить в неё задания, передать поставку в доставку, распечатать QR-код поставки, отменить задание. Программа подключена к WB только на чтение.',
      'Страница листа с кнопками «Этикетки (N)» и «Собрано по листу» — «Листы подбора» → «Открыть» (или «Открыть лист» в боковой панели).',
      'Не откладывайте «Передать курьеру». Если WB уже перевёл задание в доставку, а у нас оно ещё «собран», программа при следующем обновлении спишет товар сама, но документа «Отгрузка» (и строки в документах для 1С) по такому заданию не будет.',
      'Стикер WB привязан к номеру задания — клейте строго на свой пакет.',
    ],
  },
  {
    id: 'fbs-ozon',
    title: 'Заказ FBS Ozon: от поступления до отгрузки',
    group: 'Заказы FBS',
    routes: [],
    roles: 'менеджер, комплектовщик, администратор',
    summary: 'Полный путь отправления Ozon по шагам — так, как склад работает сейчас: менеджер за компьютером делает лист подбора, нажимает «Собрать» в кабинете Ozon, печатает лист и этикетки; комплектовщик собирает по бумажному листу; менеджер отмечает сборку, упаковку и передачу курьеру.',
    steps: [
      {
        title: '1. Отправление пришло само',
        text: 'Сервер каждую минуту читает кабинет Ozon. Новое отправление сразу резервирует товар и появляется на экране у всех. Где смотреть: «Заказы FBS → Заказы», переключатель «Ozon», вкладка «Ожидают сборки». В «Статусе»: «у нас: товар в резерве», «на площадке: ожидает сборки». «Обновить сейчас» — не дожидаясь минуты.',
      },
      {
        title: '2. Деление по штукам',
        text: 'Если в отправлении несколько штук или товаров, программа сама делит его на задания по одной штуке: в строке видно «часть N из M», номер отправления общий. Делится только то, что ещё не попало в лист подбора. В кабинете Ozon отправление остаётся одним — и этикетка на него одна, поэтому все части кладут в один пакет.',
      },
      {
        title: '3. Срок и тариф',
        text: 'Колонка «Отгрузить до» — срок из Ozon (оранжевый — меньше 6 часов, красный — просрочен). Ниже строка «тариф: …» — текущий тариф отгрузки; наведите мышь — видно, какой тариф начнётся и когда, если не успеть. Строка тарифа становится оранжевой, когда до смены тарифа меньше 6 часов.',
      },
      {
        title: '4. Проверить вкладку «Проблемы»',
        text: 'Как у WB: «нет карточки в номенклатуре» (ссылка ведёт в «Связи с WB / Ozon»), «не хватает товара — резерв не встал», «состав набора не подтверждён», «просрочен срок отгрузки». Исправили — «Зарезервировать».',
      },
      {
        title: '5. Выбрать отправления и создать лист подбора',
        text: 'Отметьте отправления галочками → «Лист подбора из выбранных (N)», или «Собрать волну» → срок («до 12:00 сегодня», «до 18:00 сегодня», «до конца дня», «до 12:00 завтра», «Все готовые»). В лист Ozon попадают только отправления Ozon. У отправлений статус «в подборе · номер листа».',
      },
      {
        title: '6. В кабинете Ozon: нажать «Собрать»',
        text: 'Ozon отдаёт этикетку только на отправление, собранное в его кабинете. Поэтому до печати этикеток найдите в кабинете Ozon отправления из листа (кнопка ⧉ у номера в нашей строке копирует его для поиска) и нажмите «Собрать». Программа этого не делает. Через минуту у нас они переедут во вкладку «Ожидают отгрузки» (на площадке: «ожидает отгрузки»).',
      },
      {
        title: '7. Открыть лист',
        text: 'После создания одного листа его страница открывается сама. Позже — «Заказы FBS → Листы подбора» → «Открыть» в строке листа. На странице листа кнопки «Печать», «Этикетки (N)» и «Собрано по листу».',
      },
      {
        title: '8. Распечатать лист подбора',
        text: 'Кнопка «Печать» на странице листа (или в «Листы подбора» в строке листа). На листе — блоки по артикулам (массовые сверху), номера отправлений с квадратиком для отметки, ячейки. «нет в ячейках» — товара нет на хранении.',
      },
      {
        title: '9. Распечатать этикетки Ozon',
        text: 'Кнопка «Этикетки (N)» на странице листа. Программа получает у Ozon готовый PDF с этикетками (в Ozon ничего не меняется) и открывает его в новой вкладке — печатайте из просмотрщика PDF на термопринтере. Одна этикетка на отправление, даже если частей несколько. Если в кабинете ещё не нажали «Собрать» (шаг 6) — будет красное «Этикетки не получены» с объяснением; если не пришла часть — оранжевое сообщение. Если Ozon долго готовит файл — нажмите «Этикетки» ещё раз через минуту.',
      },
      {
        title: '10. Отдать комплектовщику',
        text: 'Отдайте лист и этикетки комплектовщику. Он берёт товар из ячеек по листу, кладёт каждое отправление (все его части) в отдельный пакет, клеит этикетку с номером этого отправления и ставит отметку в квадратике. Собранные пакеты несёт в зону отгрузки. Не нашёл товар — пишет на листе.',
      },
      {
        title: '11. Отметить лист собранным',
        text: 'Менеджер открывает лист → «Собрано по листу» → в окне «Собрано по бумажному листу» сверьте список с отметками на бумаге → «Всё собрано». Отправления становятся «собран», товар по учёту — в зоне отгрузки. Если чего-то не нашли — «Всё собрано» не нажимайте: щёлкайте строки по одной → «Подтвердить строку» для найденного, «Не нашли всё количество — недобор» → «Закрыть недобором» для ненайденного. Отправление, собранное в кабинете Ozon, но без товара, нужно решить в кабинете Ozon (отменить) — программа этого не сделает.',
      },
      {
        title: '12. Упаковка',
        text: '«Заказы FBS → Упаковка» → «Ozon» → «Ждут упаковки». Части одного отправления («часть N из M») стоят подряд. Колонка «Короб» подсказывает коробку. Потерялась этикетка — «Этикетка» в строке. Отметьте → «Упаковано (N)». Остаток это не меняет.',
      },
      {
        title: '13. В кабинете Ozon: отгрузка и акт',
        text: 'В кабинете Ozon сформируйте отгрузку на нужную дату и распечатайте из кабинета акт приёма-передачи и, если нужно, накладную Ozon. Программа их не формирует.',
      },
      {
        title: '14. Передать курьеру',
        text: 'Когда водитель Ozon забирает груз (или вы сдаёте его в пункт приёма): на странице «Упаковка» отметьте отправления → «Передать курьеру (N)» → подтвердите. Товар списывается, появляется документ в «Отгрузки», отправление «у нас: отгружен».',
      },
      {
        title: '15. Накладная (ТТН)',
        text: '«Заказы FBS → Накладные» → «Создать накладную» → выберите отгрузку из шага 14 → проверьте реквизиты, водителя, подписантов, места и массу → «Создать и распечатать». Или бланк Ozon: «Загрузить из кабинета» → «Ozon» → PDF из кабинета → «Загрузить» → «Внести информацию» → «Сохранить» → «Печать заполненной».',
      },
      {
        title: '16. Следить за доставкой',
        text: 'Статусы Ozon программа читает сама: «Доставляются», потом «Доставлены» или «Отменены». Если через 12 часов после нашей отгрузки Ozon всё ещё не принял отправление, на «Главной» появится «Отгружены, но площадка не приняла» — проверьте в кабинете Ozon.',
      },
      {
        title: '17. Пункт приёма не взял часть',
        text: '«Отгрузки» → открыть отгрузку → отметить отправления → «Причина» → «Не принято ПВЗ (N)». Они снова «собран», товар в зоне отгрузки, резерв сохранён — отгрузите новой отгрузкой.',
      },
      {
        title: '18. Если отправление отменили',
        text: 'Отмена в Ozon приходит сама в течение минуты; у нас снять резерв может менеджер кнопкой «Отменить» (отменить в Ozon — только в кабинете). Не собрано — резерв снимается. Уже собрано — товар сразу снова в продаже, в ячейке, откуда его собрали. Комплектовщику: забрать пакет из зоны отгрузки, снять этикетку, разложить товар по местам. Подробнее — «Отмена собранного заказа».',
      },
    ],
    tips: [
      'Порядок для Ozon: лист подбора → «Собрать» в кабинете Ozon → «Этикетки (N)» у нас. Наоборот не получится — Ozon не даст этикетку.',
      'Только в кабинете Ozon: нажать «Собрать», сформировать отгрузку, акт и накладную Ozon, отменить отправление. Программа подключена к Ozon только на чтение.',
      'Деление по штукам — только внутри нашей программы. В кабинете Ozon ничего не делится: одно отправление — одна этикетка — один пакет.',
      'Не откладывайте «Передать курьеру». Если Ozon уже перевёл отправление в доставку, а у нас оно ещё «собран», программа спишет товар сама, но без документа «Отгрузка» и без строки в документах для 1С.',
    ],
  },
  {
    id: 'return-tasks',
    title: 'Отмена собранного заказа',
    group: 'Заказы FBS',
    routes: ['/return-tasks'],
    roles: 'комплектовщик — разложить товар; менеджер — видеть историю',
    summary: 'Что происходит, если заказ WB или Ozon отменили, когда он уже собран и лежит в зоне отгрузки. Товар сразу снова в продаже — в той ячейке, откуда его собрали. Подтверждать ничего не нужно, отдельного пункта меню для этого больше нет.',
    steps: [
      {
        title: 'Откуда берётся отмена',
        text: 'Площадка отменила заказ (покупатель, сама площадка, автоотмена WB) — программа узнаёт об этом в течение минуты. Или менеджер нажал «Отменить» в строке заказа в «Заказы FBS → Заказы».',
      },
      {
        title: 'Что делает программа сама',
        text: 'Резерв снимается, заказ становится «отменён». Собранный товар по учёту переносится из зоны отгрузки обратно в ту ячейку, откуда его взяли при сборке, и сразу доступен для продажи и для других заказов. Если собирали из нескольких ячеек — возвращается в каждую, сколько оттуда взяли.',
      },
      {
        title: 'Что делает комплектовщик',
        text: 'Найдите пакет этого заказа в зоне отгрузки, снимите или перечеркните стикер / этикетку площадки, достаньте товар и положите в ячейку, откуда его собирали. Номер заказа виден в «Заказы FBS → Заказы», вкладка «Отменённые» (у Ozon — «Отменены»). Где лежит товар по учёту — «Остатки» → щёлкнуть товар → «По ячейкам».',
      },
      {
        title: 'Если заказ отменили во время сборки',
        text: 'Если площадка отменила заказ, пока он в листе подбора, программа его не трогает, чтобы не мешать сборке. Отметьте лист как обычно («Собрано по листу» → «Всё собрано») — после этого товар вернётся в ячейку автоматически, а пакет отменённого заказа нужно разобрать.',
      },
      {
        title: 'История автовозвратов',
        text: 'В меню этого экрана нет, но его можно открыть по адресу /return-tasks (или кнопкой «Открыть экран →» ниже) — заголовок «Отменённые собранные заказы». Переключатель «Закрыты»: заказ, товар, количество, причина отмены и когда вернули. Во «Открыты» задачи обычно не появляются.',
      },
    ],
    tips: [
      'Если по учёту в зоне отгрузки товара меньше, чем в заказе (его уже переложили), возвращается столько, сколько там было. Разницу покажет «Остатки → Инвентаризация → Сверка остатков».',
      'Если ячейку, откуда собирали, найти не удалось, товар возвращается «без ячейки» (NOCELL) — он всё равно в продаже; положите его на свободное место и скажите менеджеру.',
      'Уже отгруженный заказ так не отменяется: если его вернул пункт приёма — «Отгрузки» → «Не принято ПВЗ», если вернул покупатель — «Приёмка → Возвраты от покупателей».',
      'Отмену заказа на самой площадке программа не делает — это делается в кабинете.',
    ],
  },
  {
    id: 'my-tasks',
    title: 'Мои задания',
    group: 'Заказы FBS',
    routes: ['/my-tasks'],
    roles: 'все',
    summary: 'Открытые листы подбора склада. Отсюда открывается страница листа: печать листа и этикеток, отметка «Собрано по листу», подтверждение отдельных строк.',
    steps: [
      {
        title: 'Выбрать фильтр',
        text: 'Переключатель «Все» / «Мои» — все открытые листы склада или только закреплённые за вами. Менеджеру обычно нужен «Все».',
      },
      {
        title: 'Прочитать карточку',
        text: 'Метка «подбор», номер листа, полоска прогресса, срок (оранжевый — скоро, красный — просрочен) и «занимается: …», если лист уже кто-то собирает.',
      },
      {
        title: 'Открыть лист',
        text: 'Щёлкните по карточке — откроется страница листа. Вверху: «Печать» (бумажный лист подбора), «Этикетки (N)» (стикеры WB или этикетки Ozon на все заказы листа), «Собрано по листу». Ниже — прогресс и карточки несобранных строк: товар, заказ, ячейки, «осталось».',
      },
      {
        title: 'Весь лист собран',
        text: 'Комплектовщик вернул лист, всё найдено: «Собрано по листу» → проверьте список в окне «Собрано по бумажному листу» → «Всё собрано». Все заказы листа — «собран», товар — в зоне отгрузки.',
      },
      {
        title: 'Собрано не всё',
        text: 'Щёлкните по строке → проверьте «Ячейка» (программа проверит, что товар там есть) и «Количество» → «Подтвердить строку». Если товара не хватило — «Не нашли всё количество — недобор», укажите «Сколько по факту нашли» и «Причина недобора» → «Закрыть недобором».',
      },
    ],
    tips: [
      'Список обновляется сам каждые 15 секунд. Новые листы появляются, когда менеджер собирает волну или создаёт лист подбора.',
      'Заказ становится «собран», когда подтверждена последняя строка этого заказа.',
      'Поле «Причина» в окне «Собрано по бумажному листу» нужно, только если в «Настройки → Режимы подтверждения» включена сборка со сканом.',
    ],
  },
  {
    id: 'pick-lists',
    title: 'Листы подбора',
    group: 'Заказы FBS',
    routes: ['/pick-lists'],
    roles: 'все; распустить лист — менеджер, администратор',
    summary: 'Журнал всех листов подбора: сколько заказов, сколько осталось собрать, ближайший срок, кто собирает. Печать бумажного листа и роспуск ошибочного.',
    steps: [
      {
        title: 'Выбрать листы',
        text: 'Переключатель «Не собраны» / «Собраны» / «Все».',
      },
      {
        title: 'Посмотреть лист',
        text: 'Щёлкните по строке — справа откроется просмотр: «Что взять» (ячейки с остатком, артикул, сколько взять; красное «нет в ячейках» — товара нет в ячейках хранения) и «Заказы — в порядке сборки».',
      },
      {
        title: 'Распечатать',
        text: 'Кнопка «Печать» в строке или в просмотре листа — бумажный лист для комплектовщика.',
      },
      {
        title: 'Этикетки и отметка сборки',
        text: 'Этикеток и кнопки «Собрано по листу» в этом просмотре нет. Они на странице листа: «Заказы FBS → Мои задания» → карточка листа.',
      },
      {
        title: 'Распустить ошибочный лист',
        text: 'Пока по листу ничего не собрано, менеджер может нажать «Распустить». Заказы вернутся в очередь, резерв сохранится.',
      },
    ],
    tips: [
      'Листы создаются в «Заказы FBS → Заказы» кнопками «Собрать волну» или «Лист подбора из выбранных».',
      'Листы WB и Ozon всегда отдельные.',
      'Весь путь заказа — разделы «Заказ FBS Wildberries…» и «Заказ FBS Ozon…».',
    ],
  },
  {
    id: 'packing',
    title: 'Упаковка',
    group: 'Заказы FBS',
    routes: ['/packing'],
    roles: 'менеджер, комплектовщик, администратор',
    summary: 'Собранные заказы: какой короб, повторная печать этикеток площадки, отметка «упаковано» и передача курьеру.',
    steps: [
      {
        title: 'Выбрать площадку',
        text: 'Переключатели «Wildberries» / «Ozon» и «Ждут упаковки» / «Упакованы». Здесь только заказы в статусе «собран».',
      },
      {
        title: 'Проверить короб',
        text: 'Колонка «Короб» показывает, в какую упаковку класть («заводская коробка» — отдельная не нужна, «не задан» — правило не настроено).',
      },
      {
        title: 'Этикетки (если нужно ещё раз)',
        text: 'Обычно этикетки уже напечатаны вместе с листом подбора. Если этикетка потерялась или испортилась — отметьте заказы и нажмите «Этикетки (N)» или «Этикетка» в строке. Файл откроется в новой вкладке — распечатайте. Если площадка отдала не все этикетки, появится оранжевое сообщение.',
      },
      {
        title: 'Отметить упакованными',
        text: 'Нажмите «Упаковано (N)» для отмеченных или «Упаковано» в строке. Заказ перейдёт в «Упакованы», видно, кто упаковал.',
      },
      {
        title: 'Передать курьеру',
        text: 'Когда груз забирают: отметьте отдаваемые заказы (в «Упакованы» или в «Ждут упаковки») → «Передать курьеру (N)» → подтвердите. Товар спишется со склада, появится документ в «Отгрузки», заказ — «отгружен». Затем сделайте накладную в «Накладные».',
      },
    ],
    tips: [
      'Ozon даёт этикетку только после того, как отправление собрано в его кабинете (перейдёт в «Ожидают отгрузки»).',
      'WB даёт стикер только на задание, добавленное в поставку, и не даёт на отгруженные или отменённые.',
      'В одной печати этикеток должна быть одна площадка.',
      'Отметка «Упаковано» — для порядка на складе; остаток она не меняет. Передать курьеру можно и без неё.',
      'После «Передать курьеру» не забудьте кабинет: WB — передать поставку в доставку, Ozon — отгрузка и акт.',
    ],
  },
  {
    id: 'shipments',
    title: 'Отгрузки',
    group: 'Заказы FBS',
    routes: ['/shipments'],
    roles: 'все смотрят; документы для 1С — менеджер, администратор',
    summary: 'Журнал передач собранных заказов курьеру площадки. Здесь же документы для 1С и возврат заказов, которые не принял пункт приёма.',
    steps: [
      {
        title: 'Как появляется отгрузка',
        text: '«Заказы FBS → Упаковка» → отметить собранные заказы → «Передать курьеру (N)» → подтвердить. Каждая такая передача — одна отгрузка. Товар списывается со склада.',
      },
      {
        title: 'Выбрать период',
        text: 'Переключатель «Сегодня», «Вчера», «7 дней» или «Период» (даты «С» и «По»).',
      },
      {
        title: 'Документы для 1С',
        text: '«Документы для 1С за период» — Excel с «Реализацией» и «Комплектацией» по всем отгрузкам периода. Для одной отгрузки — открыть её и нажать «Документы для 1С».',
      },
      {
        title: 'Проверить отгрузку',
        text: 'Щёлкните по строке: плашка показывает число заказов и штук в реализации и «Сходится с заказами» (зелёная) или «Не сходится» (красная).',
      },
      {
        title: 'Не принято ПВЗ',
        text: 'Если пункт приёма не взял часть заказов: в открытой отгрузке отметьте их галочками, проверьте «Причина» (по умолчанию «Акт расхождения ПВЗ») и нажмите «Не принято ПВЗ (N)». Заказы вернутся в «собран», товар — в зону отгрузки, резерв сохранится. Отгрузите их потом новой отгрузкой.',
      },
    ],
    tips: [
      'Количество в «Реализации» всегда равно количеству отгруженных заказов — документ строится из самих заказов.',
      'Такие возвраты видны в колонке «Акты расхождения» и в таблице «Акты расхождения» внутри отгрузки.',
      'По отгрузке делается ТТН: «Накладные» → «Создать накладную» → выбрать отгрузку.',
    ],
  },
  {
    id: 'waybills',
    title: 'Накладные',
    group: 'Заказы FBS',
    routes: ['/waybills'],
    roles: 'создают и печатают все; удалить — менеджер, администратор',
    summary: 'ТТН на перевозку до склада площадки: своя накладная по отгрузке или бланк, скачанный из кабинета, с дописанным номером, машиной и подписантами.',
    steps: [
      {
        title: 'Создать накладную по отгрузке',
        text: '«Создать накладную» → в «Отгрузка» выберите отгрузку — места и масса посчитаются сами (или оставьте пустым и заполните вручную).',
      },
      {
        title: 'Заполнить реквизиты',
        text: 'Проверьте «Серия», «Номер» (подсказка показывает прошлый номер), «Дата», «Грузополучатель», «Пункт разгрузки», «Основание отпуска», «Автомобиль». Выберите «Водитель», «Отпуск разрешил», «Товар принял», «Сдал грузоотправитель» из списка подписантов. Проверьте «Грузовых мест» и «Масса груза, кг».',
      },
      {
        title: 'Сохранить и распечатать',
        text: '«Создать и распечатать» — накладная сохранится и сразу откроется печать.',
      },
      {
        title: 'Накладная из кабинета',
        text: '«Загрузить из кабинета» → выберите «Ozon» или «Wildberries» → PDF, скачанный в кабинете → «Загрузить». Программа прочитает дату, грузополучателя, места, массу и суммы. Затем в строке нажмите «Внести информацию», проставьте номер, водителя и подписантов → «Сохранить».',
      },
      {
        title: 'Печать и выгрузка',
        text: '«Печать» (для загруженной — «Печать заполненной»: бланк кабинета с вписанными данными), «Пустой бланк» — исходный файл кабинета, «Excel» — накладная в Excel. После печати статус меняется с оранжевого «не распечатана» на «распечатана».',
      },
      {
        title: 'Удалить',
        text: 'Менеджер может удалить ошибочную накладную кнопкой «Удалить» (вместе с загруженным файлом). Отменить удаление нельзя.',
      },
    ],
    tips: [
      'Реквизиты организации, машина по умолчанию и список подписантов задаются в «Справочники → Контрагенты → Наша организация».',
      'Номер без проставленного значения подсвечивается оранжевым «номер не проставлен».',
    ],
  },
  {
    id: 'order-diffs',
    title: 'Изменившиеся заказы',
    group: 'Заказы FBS',
    routes: ['/order-diffs'],
    roles: 'менеджер, администратор',
    summary: 'Заказы, у которых площадка при повторной загрузке прислала другой состав или количество. Программа сама ничего не меняет — только показывает «было / стало».',
    steps: [
      {
        title: 'Открыть список',
        text: 'Переключатель «Открытые», «Решённые», «Отклонённые». В строке — заказ, площадка, когда обнаружено, «Было», «Стало», статус.',
      },
      {
        title: 'Взять в разбор',
        text: 'У нового расхождения нажмите «Взять в разбор» — статус «В разборе».',
      },
      {
        title: 'Выяснить',
        text: 'Проверьте заказ в кабинете площадки и на складе (в окне видно «Заказ на складе сейчас: …»). Если нужно, поправьте сборку вручную: отмените заказ, распустите лист и т. п.',
      },
      {
        title: 'Закрыть',
        text: 'Нажмите «Решить» → впишите «Комментарий» (что выяснили / сделали) → «Решено» или «Отклонить».',
      },
    ],
    tips: [
      'Состав и резерв заказа система не меняет сама ни при каких обстоятельствах — только человек отдельным действием.',
    ],
  },
  {
    id: 'problems',
    title: 'Центр проблем',
    group: 'Заказы FBS',
    routes: ['/problems'],
    roles: 'все',
    summary: 'Одна очередь всего, что требует внимания: заказы без карточки, просрочки, неподтверждённые наборы, изменившиеся заказы, недобор, ошибки обмена. Открывается значком ⚠ в шапке.',
    steps: [
      {
        title: 'Открыть',
        text: 'Нажмите ⚠ в верхней строке. Красное число на значке — сколько проблем сейчас.',
      },
      {
        title: 'Прочитать строку',
        text: '«Приоритет» (высокий, средний, низкий), «Тип» (Нет карточки, Просрочка, Набор не подтверждён, Заказ изменился, Недобор, Ошибка синхронизации), «Что» и «Когда» (срок).',
      },
      {
        title: 'Решить',
        text: 'Щёлкните ссылку в последней колонке — она откроет нужный объект и подскажет действие.',
      },
    ],
    tips: [
      'Список обновляется сам каждые 30 секунд; проблема исчезает, когда её причина устранена.',
    ],
  },

  {
  "id": "supplies",
  "title": "Поставки FBO и FBW",
  "group": "Поставки FBO / FBW",
  "routes": [
    "/supplies"
  ],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Выбор площадки, реквизиты, состав, резерв и подготовка товара к упаковке.",
  "steps": [
    {
      "title": "Выберите площадку и поставку",
      "text": "Откройте «Поставки FBO / FBW». Нажмите «Ozon FBO» или «Wildberries FBW»: список и поиск товаров относятся к выбранной площадке. Выберите «В работе», «Отгружены», «Отменены» или «Все». Нажмите номер поставки. После «Статус» показан «Комментарий» поставки; наведите на сокращённый текст, чтобы прочитать его полностью.",
      "image": "/manual/img/20261103-supplies.png",
      "imageAlt": "Выберите площадку и поставку — экран программы"
    },
    {
      "title": "Рассчитайте что довезти",
      "text": "На вкладке «Что довезти» задайте дни в пути и запас в днях. «Обновить остатки и продажи площадок» читает данные площадок. Категории отбирают строки, стрелки в заголовках меняют порядок сортировки. Проверьте «Везти» и галочки строк перед созданием поставки. Расчёт — рекомендация, а не подтверждённый заказ площадки.",
      "image": "/manual/img/20261103-plan.png",
      "imageAlt": "Рассчитайте что довезти — экран программы"
    },
    {
      "title": "Заполните данные поставки",
      "text": "«Новая поставка» создаётся для выбранной площадки. В блоке «Данные поставки» укажите кабинет, склад назначения, дату отгрузки и комментарий. Нажмите «Сохранить», когда меняете реквизиты. В открытой поставке видна только её площадка. Блоки можно сворачивать; шапка показывает число товаров, единиц и коробов.",
      "image": "/manual/img/20261103-header.png",
      "imageAlt": "Заполните данные поставки — экран программы"
    },
    {
      "title": "Добавьте товар и зарезервируйте строку",
      "text": "В «Состав поставки» ищите по артикулу, названию или штрихкоду нужной площадки. Сверьте название результата перед выбором. Товар добавляется без резерва; количество незарезервированной строки сохраняется при выходе из поля. Нажмите «Зарезервировать» в строке. Для изменения зарезервированного количества сначала снимите резерв; если товар уже в коробах, сначала уберите его оттуда. Красная корзина удаляет незарезервированную строку.",
      "image": "/manual/img/20261103-composition.png",
      "imageAlt": "Добавьте товар и зарезервируйте строку — экран программы"
    },
    {
      "title": "Соберите товар по ячейкам",
      "text": "Блок «Что взять со склада» показывает ячейки, артикулы, наименования и количество составляющих. У набора список может содержать компоненты и упаковку вместо готового набора. Не путайте его с таблицей «Состав поставки». Если ячейка не указана или товара не хватает, выясните причину до отгрузки.",
      "image": "/manual/img/20261103-pick.png",
      "imageAlt": "Соберите товар по ячейкам — экран программы"
    },
    {
      "title": "Отгрузите и сверьте приёмку площадкой",
      "text": "После проверки состава и коробов нажмите «Отгружено» и подтвердите списание. В отгруженной поставке блок «Приёмка площадкой»: для WB укажите номер поставки, сохраните и нажмите «Сверить с WB». Для Ozon укажите номер заявки и внесите принятые количества по акту вручную. «Сверка закончена» прекращает учёт поставки как находящейся в пути. До подтверждения проверьте каждую строку, не принимайте автоматически подставленные количества за фактическую приёмку.",
      "image": "/manual/img/20261103-header.png",
      "imageAlt": "Отгрузите и сверьте приёмку площадкой — экран программы"
    }
  ],
  "tips": [
    "Создание поставки и резерв в WMS не создают поставку в кабинете площадки.",
    "Если артикул или штрихкод не найден, проверьте выбранную площадку, кабинет и связь карточки с товаром в WMS. Не выбирайте похожий товар вместо нужного.",
    "«Отгружено» списывает товар после подтверждения. Кнопка «Отменить» отменяет поставку и снимает резерв; она не закрывает окно."
  ]
},
  {
  "id": "supply-boxes",
  "title": "Короба и сроки годности",
  "group": "Поставки FBO / FBW",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Создание и изменение коробов, количество, проверка полной раскладки и комментарии.",
  "steps": [
    {
      "title": "Создайте или выберите короб",
      "text": "В блоке «Короба» нажмите «Новый короб». Для существующего короба нажмите «Добавить» или «Состав». Откроется отдельное окно по центру. Таблица показывает номер нашего короба, артикулы, кол-во, состав, стикеры, статус и комментарий. Для WB под номером виден сохранённый ШК WB; этот код нужен для сверки с кабинетом, где порядок коробов может отличаться.",
      "image": "/manual/img/20261103-boxes.png",
      "imageAlt": "Создайте или выберите короб — экран программы"
    },
    {
      "title": "Найдите зарезервированный товар",
      "text": "В окне состава доступны зарезервированные товары поставки. Поиск — по названию, штрихкоду или артикулу. «Показать только добавленные» оставляет строки текущего короба. «Остаток» — сколько ещё можно добавить с учётом других коробов; ниже показано всё количество строки поставки.",
      "image": "/manual/img/20261103-box-edit.png",
      "imageAlt": "Найдите зарезервированный товар — экран программы"
    },
    {
      "title": "Введите количество по кнопке Добавить",
      "text": "В столбце «В коробе» сначала видна кнопка «Добавить». Нажмите её: появится поле количества. Введите итоговое количество этого товара в текущем коробе и нажмите ✓ или Enter; × отменяет ввод. Ноль убирает товар из короба. «Добавить весь остаток» заполняет все доступные остатки. Пока ввод количества не подтверждён, сохранить состав нельзя.",
      "image": "/manual/img/20261103-quantity.png",
      "imageAlt": "Введите количество по кнопке Добавить — экран программы"
    },
    {
      "title": "Укажите срок годности и сохраните",
      "text": "Для каждой строки с положительным количеством заполните «Годен до». Без срока годности кнопка «Сохранить состав» недоступна. Нажмите её после проверки количества и дат. Положительное количество и корректные сроки переводят короб в «Готов к отгрузке». До сохранения изменения остаются только в окне; «Отменить» закрывает редактор без сохранения состава.",
      "image": "/manual/img/20261103-box-edit.png",
      "imageAlt": "Укажите срок годности и сохраните — экран программы"
    },
    {
      "title": "Проверьте раскладку и состав",
      "text": "Показатель «Разложено» считает товар полностью разложенным, только если всё его количество распределено по коробам. «3 из 4 товаров» — число полностью разложенных артикулов, а следующая пара чисел — количество единиц. Нажмите «Короб № …», чтобы посмотреть сохранённый состав, количество и сроки, не открывая редактор.",
      "image": "/manual/img/20261103-box-view.png",
      "imageAlt": "Проверьте раскладку и состав — экран программы"
    },
    {
      "title": "Добавьте комментарий к коробу",
      "text": "Нажмите комментарий в строке короба. В отдельном окне введите текст и нажмите «Сохранить комментарий». В таблице текст занимает одну строку; при наведении показывается полностью. Красная корзина в конце строки удаляет короб после подтверждения: его товары остаются в резерве поставки и доступны для других коробов.",
      "image": "/manual/img/20261103-comment.png",
      "imageAlt": "Добавьте комментарий к коробу — экран программы"
    }
  ],
  "tips": [
    "Нельзя положить товар сверх количества поставки или сверх доступного остатка для этого короба.",
    "Если поставка изменена другим сотрудником, обновите данные и проверьте состав заново. Сообщение об ошибке не означает сохранение."
  ]
},
  {
  "id": "supply-print",
  "title": "Лист подбора и товарные стикеры",
  "group": "Поставки FBO / FBW",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Предпросмотр, печать и скачивание листа подбора; стикеры на короб и отдельный артикул.",
  "steps": [
    {
      "title": "Откройте лист подбора",
      "text": "В блоке «Состав поставки» нажмите «Лист подбора». Откроется PDF с таблицами «Состав поставки» и «Что взять со склада». Миниатюры слева и номер страницы переключают страницы; −, + и «По ширине страницы» меняют масштаб. Печать автоматически не начинается.",
      "image": "/manual/img/20261103-pdf.png",
      "imageAlt": "Откройте лист подбора — экран программы"
    },
    {
      "title": "Распечатайте или скачайте PDF",
      "text": "В верхней панели предпросмотра нажмите значок принтера для печати или значок скачивания для PDF. Скачанный файл содержит тот же лист, который виден в просмотре. Количество выровнено по центру, таблицы имеют правую границу. Для поставки Ozon лист также показывает доступные предельные цены RUB и пересчёт BYN с указанным источником курса; отсутствующая цена не заменяется выдуманной.",
      "image": "/manual/img/20261103-pdf.png",
      "imageAlt": "Распечатайте или скачайте PDF — экран программы"
    },
    {
      "title": "Напечатайте комплект стикеров короба",
      "text": "В строке готового короба нажмите «Печать стикеров». Комплект начинается с номера короба и обозначения площадки; затем идёт состав, не более пяти артикулов на стикере, и товарные стикеры. Длинный артикул уменьшается до одной строки, при малом числе позиций шрифт состава крупнее. Срок годности товарного стикера берётся из сохранённого состава короба.",
      "image": "/manual/img/20261103-boxes.png",
      "imageAlt": "Напечатайте комплект стикеров короба — экран программы"
    },
    {
      "title": "Печатайте стикеры одного артикула",
      "text": "Нажмите номер короба. В просмотре состава нажмите «Печать стикеров» у нужного товара, проверьте изображение и «Количество стикеров», затем нажмите «Печать». Печать не меняет количество товара в коробе. Если стикер недоступен, прочитайте причину: проверьте срок, единицу измерения, связь с площадкой и данные шаблона.",
      "image": "/manual/img/20261103-box-view.png",
      "imageAlt": "Печатайте стикеры одного артикула — экран программы"
    }
  ],
  "tips": [
    "«Состав» рядом с «Лист подбора» — отдельная выгрузка Excel. Для Wildberries в ней «Баркод» и «Количество», баркод выбирается из карточки WB с началом на 2; для Ozon сохраняется свой формат.",
    "Не придумывайте штрихкод и состав товара, если они не найдены. Сверьте карточку площадки и данные производителя.",
    "Кнопка «Печать стикеров» в строке короба и «Этикетки коробов 76 × 120» под таблицей — разные документы."
  ]
},
  {
  "id": "supply-wb-template",
  "title": "Шаблон коробов Wildberries",
  "group": "Поставки FBO / FBW",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Заполнение Excel WB по сохранённым коробам WMS с повтором ШК для каждой позиции.",
  "steps": [
    {
      "title": "Загрузите шаблон из кабинета WB",
      "text": "В кабинете нужной поставки WB откройте «Через Excel → Скачать шаблон со штрихкодами». В WMS выберите эту поставку и под таблицей коробов нажмите «Загрузить шаблон WB». Выберите .xlsx до 4 МБ. Не используйте шаблон другой поставки.",
      "image": "/manual/img/20261103-wb-template-upload.png",
      "imageAlt": "Загрузите шаблон из кабинета WB — экран программы"
    },
    {
      "title": "Проверьте заполнение",
      "text": "Нажмите «Проверить и заполнить». Сверьте «Короб WMS», «ШК короба WB», артикул, баркод товара, количество и срок годности. Для нескольких позиций одного короба его ШК повторяется в каждой строке. Дата выгружается как ДД.ММ.ГГГГ. Сначала используются сохранённые соответствия; первоначально короба сопоставляются по порядку шаблона.",
      "image": "/manual/img/20261103-wb-template-ready.png",
      "imageAlt": "Проверьте заполнение — экран программы"
    },
    {
      "title": "Скачайте и загрузите заполненный Excel",
      "text": "Нажмите «Скачать заполненный Excel». Соответствия ШК сохранятся под номерами коробов WMS. Файл загрузите в кабинет той же поставки WB самостоятельно. Порядок строк на экране WB может отличаться: проверяйте по ШК, а не по месту строки. Если поставка в WMS изменилась после проверки, проверьте шаблон заново.",
      "image": "/manual/img/20261103-wb-template-ready.png",
      "imageAlt": "Скачайте и загрузите заполненный Excel — экран программы"
    }
  ],
  "tips": [
    "Выгрузка Excel из WMS сама не загружает его в Wildberries.",
    "В Ozon используется отдельная кнопка и отдельный шаблон; файл WB для него не подходит."
  ]
},
  {
  "id": "supply-wb-labels",
  "title": "Этикетки коробов WB размером 76 на 120 мм",
  "group": "Поставки FBO / FBW",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Сверка с кабинетом WB и PDF этикеток с номером нашего короба, кодами и составом.",
  "steps": [
    {
      "title": "Введите штрихкод поставки WB",
      "text": "Под коробами нажмите «Этикетки коробов 76 × 120». Введите полный штрихкод поставки WB-GI-… и нажмите «Проверить с WB». Номер поставки и штрихкод — разные значения. Программа использует сохранённое соответствие, получает данные WB и сверяет состав.",
      "image": "/manual/img/20261103-wb-label-entry.png",
      "imageAlt": "Введите штрихкод поставки WB — экран программы"
    },
    {
      "title": "Выполните первичную настройку при необходимости",
      "text": "Если штрихкод ещё неизвестен программе, откройте «Настройки». Укажите номер поставки WB для первой привязки либо загрузите PDF WB. Наш УНП подставляется автоматически, его можно заменить. Шаблон коробов повторно не нужен, если соответствия уже получены из выгрузки WMS; при отсутствии кодов выберите нужный .xlsx. Не заменяйте код короба произвольным номером.",
      "image": "/manual/img/20261103-wb-label-entry.png",
      "imageAlt": "Выполните первичную настройку при необходимости — экран программы"
    },
    {
      "title": "Проверьте результат справа",
      "text": "После успешной сверки появится «Состав совпадает с WB», склад, дата и поставщик из кабинета WB. Сверьте таблицу коробов. Нажмите номер короба в таблице или выберите его справа — предпросмотр покажет его этикетку. На этикетке: сверху штрихкод поставки, в середине информация и артикулы в два столбца, внизу код короба WB и QR-код. При числе артикулов до пяти их шрифт крупнее.",
      "image": "/manual/img/20261103-wb-label-preview.png",
      "imageAlt": "Проверьте результат справа — экран программы"
    },
    {
      "title": "Скачайте этикетки и распечатайте",
      "text": "Нажмите «Скачать стикеры». Печатайте PDF на этикетке 76 × 120 мм в масштабе 100%, без подгонки под страницу. Сверьте первый отпечаток с выбранным коробом. Если данные или состав не совпали, исправьте причину и выполните проверку повторно; не печатайте по сообщению об ошибке.",
      "image": "/manual/img/20261103-wb-label-preview.png",
      "imageAlt": "Скачайте этикетки и распечатайте — экран программы"
    }
  ],
  "tips": [
    "Склад и дата для этого документа берутся из WB: поле назначения в WMS может отличаться.",
    "Если предпросмотр не появился, прочитайте сообщение в окне и обновите страницу. Не считайте пустую область готовой этикеткой."
  ]
},
  {
  "id": "supply-ozon-template",
  "title": "Шаблон коробов Ozon",
  "group": "Поставки FBO / FBW",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Заполнение шаблона Ozon по составу коробов с проверкой соответствий и сроков.",
  "steps": [
    {
      "title": "Выберите шаблон Ozon",
      "text": "В поставке Ozon под коробами нажмите «Заполнить шаблон Ozon». Загрузите .xlsx из кабинета Ozon размером до 4 МБ и нажмите «Проверить шаблон». Короба должны быть заполнены; для отменённой поставки выгрузка недоступна.",
      "image": "/manual/img/20261103-ozon-template.png",
      "imageAlt": "Выберите шаблон Ozon — экран программы"
    },
    {
      "title": "Сверьте короба и скачайте файл",
      "text": "В «Сопоставление коробов» проверьте штрихкод Ozon для каждого номера нашего короба. Один код не должен использоваться для двух коробов; при выборе занятого кода программа меняет соответствия местами. Ниже проверьте артикулы, количества, даты и зоны размещения. Затем «Скачать заполненный .xlsx» и загрузите файл в кабинет Ozon. Если поставка изменилась, сначала повторите проверку.",
      "image": "/manual/img/20261103-ozon-template.png",
      "imageAlt": "Сверьте короба и скачайте файл — экран программы"
    }
  ],
  "tips": [
    "Не переносите ШК коробов WB в шаблон Ozon.",
    "Неизвестную зону размещения можно оставить пустой; одна зона применяется ко всем строкам одного товара."
  ]
},

  // ================================================================ СПРАВОЧНИКИ
  {
    id: 'products',
    title: 'Товары и комплекты',
    group: 'Справочники',
    routes: ['/products'],
    roles: 'менеджер, администратор',
    summary: 'Номенклатура: карточки товаров и наборов, штрихкоды, состав наборов, связи с площадками, категории, правила упаковки. Загрузка из 1С.',
    steps: [
      {
        title: 'Найти товар',
        text: 'Вкладка «Номенклатура»: переключатель «Всё» / «Товары» / «Наборы», фильтр категорий, единый поиск «Название, штрихкод, артикул, поставщик». Отдельного фильтра поставщиков нет. Результат разбит на страницы; уточняйте поиск и проверяйте выбранные фильтры.',
      },
      {
        title: 'Новый товар',
        text: '«Новый товар» → «Артикул» (как у продавца на площадке), «Артикул 1С», «Ед. изм.», «Наименование», «Категория». Отметьте «Комплект», если это набор (остаток из составляющих), или «Упаковка» для коробок и пакетов → «Создать».',
      },
      {
        title: 'Карточка: учёт и закупка',
        text: '«Активен» (выключенный уходит на площадки нулём), «Страховой буфер» (столько штук не показываем на площадках), «Страховой запас» (для расчёта закупки), «Поставщик», «Срок поставки, дн», «Закупочная цена», «Минимальная партия», «Кратность заказа» (пусто — как у поставщика) → «Сохранить».',
      },
      {
        title: 'Штрихкоды',
        text: 'Вкладка «Штрихкоды» в карточке: «Добавить штрихкод» → «Добавить»; «Сделать основным»; ✕ удаляет. Штрихкод уникален во всей базе.',
      },
      {
        title: 'Состав набора',
        text: 'Вкладка «Состав комплекта»: «Добавить составляющую», укажите количество (у весовых — в граммах: 500 г = 500) → «Сохранить состав» или «Состав верный — подтвердить».',
      },
      {
        title: 'Наборы на проверке',
        text: 'Вкладка «Наборы на проверке» (оранжевое число — сколько ждут): проверьте предложенный состав → «Верно» или «Верно — все N на экране»; если неправильно — «Изменить» / «Задать».',
      },
      {
        title: 'Связь с площадкой',
        text: 'Вкладка «Маркетплейсы» в карточке — связи товара с кабинетами и что отправлено. «Связать с маркетплейсом» — как в разделе «Связи с WB / Ozon».',
      },
      {
        title: 'Категории',
        text: 'Массово: отметьте товары, выберите «Поставить категорию…» → «Выбранным» или «Всем найденным». Вкладка «Категории»: «Добавить головную категорию», «Добавить подкатегорию», «Переименовать», «Удалить», слова в названии для автоматической раскладки и «Разнести товары без категории».',
      },
      {
        title: 'Артикулы МП',
        text: 'Во вкладке «Артикулы МП» отметьте нужные строки флажками и нажмите «Применить (N)»: меняются только выбранные позиции. Флажок в шапке выбирает или снимает все однозначные предложения. В разделе «Нужно выбрать» сначала укажите артикул, затем проверьте отметку. «Обновить список» перечитывает предложения из последних загруженных карточек WB/Ozon; изменившееся предложение нужно выбрать заново. Артикул 1С не меняется.',
      },
      {
        title: 'Упаковка наборов',
        text: 'Вкладка «Упаковка»: правила, какую коробку класть в весовой набор. «Добавить правило» → «Название», «Вес набора от / до, кг», «Слово в названии набора», «Упаковка (товар)», «Штук упаковки на набор», «Порядок» → «Сохранить». Затем «Проверить» и «Применить».',
      },
      {
        title: 'Загрузить из 1С',
        text: '«Загрузить из 1С» → выберите файлы .xlsx → «Проверить» (ничего не пишет: сколько товаров новых и обновится, наборов, штрихкодов, замечаний) → «Загрузить в систему». Наборы с неясным составом попадут на проверку — «Перейти к проверке».',
      },
    ],
    tips: [
      'Пока состав набора не подтверждён, набор не участвует в остатках, резерве и выгрузке. После подтверждения ждавшие заказы резервируются сами.',
      'Если карточку одновременно сохранил коллега, программа предупредит и покажет свежую версию — внесите правки ещё раз.',
      'Коробку для правил упаковки сначала заведите товаром с отметкой «Упаковка».',
    ],
  },
  {
    id: 'parties',
    title: 'Контрагенты',
    group: 'Справочники',
    routes: ['/parties'],
    roles: 'менеджер, администратор; реквизиты организации правит администратор',
    summary: 'Поставщики, покупатели и перевозчики, а также реквизиты нашей организации и подписанты для накладных.',
    steps: [
      {
        title: 'Найти контрагента',
        text: 'Вкладка «Контрагенты»: «Поиск» (название или УНП), «Роль» (поставщик, покупатель, перевозчик). «Изменить» открывает карточку.',
      },
      {
        title: 'Добавить',
        text: '«Добавить контрагента» → «Полное наименование», «Краткое название», «УНП / ИНН», «Роли» (одна фирма может быть и поставщиком, и покупателем), телефон, почта, адрес.',
      },
      {
        title: 'Для накладных',
        text: '«Пункт разгрузки» (подставляется в ТТН для покупателя), «Договор (основание отпуска)».',
      },
      {
        title: 'Для закупки',
        text: '«Срок поставки, дн», «Минимальная партия, шт», «Кратность заказа» (например, штук в коробке), «Условия поставки». Эти числа использует «Что заказать».',
      },
      {
        title: 'Сохранить',
        text: '«Работаем» — снимите, если с контрагентом больше не работаете. «Примечание» — по желанию. Нажмите «Сохранить».',
      },
      {
        title: 'Наша организация',
        text: 'Вкладка «Наша организация»: наименование, УНП, адрес, «Пункт погрузки», телефоны, «Банк», «Расчётный счёт», «Машина по умолчанию» → «Сохранить реквизиты» (только администратор). Ниже «Подписанты документов»: «Должность», «Фамилия и инициалы» → «Добавить»; подписанта можно выключить.',
      },
    ],
    tips: [
      'Условия в карточке товара важнее условий поставщика: если у товара заполнены минимальная партия или кратность, используются они.',
    ],
  },
  {
    id: 'mapping',
    title: 'Связи с WB / Ozon',
    group: 'Справочники',
    routes: ['/mapping'],
    roles: 'менеджер, администратор',
    summary: 'Какой наш товар соответствует карточке на площадке. Без связи заказ не резервируется и остаток не выгружается. На странице заголовок «Маппинг WB / Ozon».',
    steps: [
      {
        title: 'Заказы без связи',
        text: 'Вкладка «Пришли в заказах без связи» (оранжевое число): SKU и баркод площадки, сколько заказов и штук ждут, ближайший срок. Нажмите «Сопоставить».',
      },
      {
        title: 'Заполнить связь',
        text: 'В окне «Связать товар с маркетплейсом»: «Кабинет», «Наш товар» (поиск), «nmID WB» или «SKU Ozon», «Баркод МП» (для WB нужен для выгрузки остатков), для Ozon — «offer_id (артикул в Ozon)», для WB по желанию «chrtID» → «Связать». Ждавшие заказы зарезервируются сами.',
      },
      {
        title: 'Связать заранее',
        text: 'Кнопка «Связать товар» вверху — то же окно без заказа.',
      },
      {
        title: 'Проверить связи',
        text: 'Вкладка «Связанные»: фильтр кабинета, поиск по нашему артикулу, SKU, баркоду, offer_id. Колонка «Выгрузка»: «выгрузка выключена», «ошибка», «в очереди», «актуально». Переключатель «Активен» выключает связь.',
      },
    ],
    tips: [
      'Пока выгрузка остатков выключена, в колонке «Выгрузка» будет «выгрузка выключена» — это нормально.',
    ],
  },

  // ================================================================ НАСТРОЙКИ
  {
    id: 'settings',
    title: 'Настройки',
    group: 'Настройки',
    routes: ['/settings'],
    roles: 'администратор',
    summary: 'Сотрудники и роли, склады и ячейки, подключение кабинетов WB и Ozon, режимы подтверждения, журналы.',
    steps: [
      {
        title: 'Сотрудники',
        text: 'Вкладка «Сотрудники» → «Добавить сотрудника»: «Имя и фамилия», «Email (логин)», «Роль» (кладовщик, менеджер, администратор), для кладовщика — «Склад», «Пароль» (не короче 8 символов). «Выйти везде» завершает все сеансы сотрудника. Чтобы заблокировать — снимите «Активен».',
      },
      {
        title: 'Склады, ячейки и зоны',
        text: 'Вкладка «Склады, ячейки и зоны»: «Создать склад» (код и название). Выберите «Склад» и в «Разметка ячеек» вставьте по строке на ячейку: адрес;штрихкод этикетки;код зоны (можно из Excel) → «Сохранить ячейки». Зоны: «Код», «Название», «Тип» → «Добавить зону».',
      },
      {
        title: 'Кабинеты WB / Ozon',
        text: '«Подключить кабинет» → площадка, «Название», «Наш склад», ID склада на площадке, для Ozon «Client-Id», «Ключ API» (для WB — токен категории «Маркетплейс»), «Опрос заказов, сек». В таблице видно состояние загрузки заказов и ошибки. «Изменить» — поменять настройки, «Сменить склад МП» — переезд на новый склад площадки.',
      },
      {
        title: 'Режимы подтверждения',
        text: 'Вкладка «Режимы подтверждения»: для склада, зоны и операции (подбор по строке, закрытие всего листа одним сканом, подтверждение возврата из отгрузки) выберите «Ручной», «Рекомендуется скан» или «Скан обязателен». Самая точная настройка побеждает.',
      },
      {
        title: 'Журналы',
        text: '«Журнал выгрузки» — пачки отправки остатков («Только проблемные»), щелчок — что отправили и что ответила площадка. «Журнал действий» — кто, когда и что изменил, с «Было» и «Стало».',
      },
    ],
    tips: [
      'Галочка «Выгружать остатки» и кнопка «Перевыгрузить» отправляют остатки на площадку. Сейчас выгрузка намеренно выключена — не включайте её без решения владельца.',
      'При смене роли или пароля сотрудник выходит со всех устройств.',
      'Смена склада площадки создаёт новый кабинет; по старому складу на площадку уходит 0, его заказы не теряются. Когда старые заказы отгружены — выключите старый кабинет.',
    ],
  },

  // ================================================================ ТСД И ТЕЛЕФОН
  {
    id: 'tsd',
    title: 'Режим ТСД: главный экран',
    group: 'ТСД и телефон',
    routes: ['/tsd'],
    roles: 'кладовщик, администратор',
    summary: 'Пока не используется — сборка идёт по бумажному листу. Раздел на будущее. Упрощённый экран для терминала сбора данных или телефона: крупные кнопки, сканер, звук и вибрация на каждый скан. Кладовщик попадает сюда сразу после входа.',
    steps: [
      {
        title: 'Шапка',
        text: 'Синяя полоса: название экрана, кнопка ← (назад), кружок связи (зелёный — есть, красный — нет) и на главном экране кнопка «Имя · выйти». Если связи нет, появится красная строка «Нет связи с сервером — сканы не проходят».',
      },
      {
        title: 'Мои задания',
        text: 'Вверху — «Мои задания» («Все» / «Мои»): листы подбора. Нажмите на карточку, чтобы начать.',
      },
      {
        title: 'Плитки инструментов',
        text: 'Ниже — «Приёмка», «Возвраты покупателей», «Отгрузка», «Поставки FBO», «Где товар», «Перемещение», «Инвентаризация». «Приёмка», «Возвраты покупателей», «Поставки FBO» и «Инвентаризация» открывают обычные страницы программы — они удобно работают и на телефоне.',
      },
      {
        title: 'Сканирование',
        text: 'Поле скана всегда в фокусе: сканируйте сканером ТСД или введите код и нажмите Enter. Удачный скан — короткий высокий сигнал, ошибка — двойной низкий сигнал, вибрация и красная плашка с причиной.',
      },
      {
        title: 'Камера телефона',
        text: 'На обычных страницах (приёмка, возвраты, инвентаризация, короба, оприходование) рядом с полем скана есть кнопка «Камера»: наведите камеру на штрихкод — код распознается сам.',
      },
    ],
    tips: [
      'Камера работает на Android и на iPhone, только по защищённому адресу (https).',
      'Администратор попадает в режим ТСД через «Настройки → Режим ТСД», менеджеру режим ТСД недоступен.',
      'Если кладовщик ушёл с ТСД на обычную страницу (например, «Приёмка»), вернуться можно кнопкой браузера «Назад» или открыв адрес программы заново.',
    ],
  },
  {
    id: 'tsd-pick',
    title: 'ТСД: сборка листа подбора',
    group: 'ТСД и телефон',
    routes: ['/tsd/pick', '/tsd/return'],
    roles: 'кладовщик, администратор',
    summary: 'Пока не используется — сборка идёт по бумажному листу. Раздел на будущее. Построчная сборка листа подбора на ТСД. Что делать с товаром отменённого собранного заказа — в конце раздела.',
    steps: [
      {
        title: 'Открыть лист',
        text: 'На главном экране нажмите карточку подбора в «Мои задания». Видны прогресс «N из M» и первая строка: заказ, товар, «Нужно», «Ячейка».',
      },
      {
        title: 'Скан ячейки',
        text: 'Подойдите к ячейке и отсканируйте её этикетку («Отсканируйте ячейку»). Появится «Ячейка … подтверждена».',
      },
      {
        title: 'Скан товара',
        text: 'Отсканируйте товар («Отсканируйте товар»). Если это не тот товар, программа покажет «Неверный товар» и что ожидалось.',
      },
      {
        title: 'Количество',
        text: 'Проверьте «Количество» и нажмите «Отправить». Откроется следующая строка.',
      },
      {
        title: 'Недобор',
        text: 'Если всего не нашли — «Не нашли всё — недобор» → «Сколько по факту нашли», «Причина недобора» → «Закрыть недобором». Менеджер увидит «Недобор при сборке» на «Главной».',
      },
      {
        title: 'Конец листа',
        text: 'После последней строки — «Лист собран» и кнопка «К списку листов». Прервать можно кнопкой «Приостановить — вернуться позже»: собранное сохранится, продолжить можно с любого устройства.',
      },
      {
        title: 'Отменённый собранный заказ',
        text: 'Подтверждать на ТСД ничего не нужно: программа сама вернула товар по учёту в ячейку, откуда его собрали. Заберите пакет из зоны отгрузки, снимите стикер и положите товар в эту ячейку (её покажет «Где товар»). Подробнее — раздел «Отмена собранного заказа».',
      },
    ],
    tips: [
      'Собранный товар перемещается в зону отгрузки, заказ становится «собран», когда собрана его последняя строка.',
      'Если связь оборвалась в момент отправки, отправьте ещё раз — двойного списания не будет.',
    ],
  },
  {
    id: 'tsd-ship',
    title: 'ТСД: отгрузка курьеру и поставки FBO',
    group: 'ТСД и телефон',
    routes: ['/tsd/ship', '/tsd/supplies'],
    roles: 'кладовщик, администратор',
    summary: 'Пока не используется — сборка идёт по бумажному листу. Раздел на будущее. Передача собранных заказов курьеру площадки и отгрузка поставок на склады площадок.',
    steps: [
      {
        title: 'Открыть «Отгрузка»',
        text: 'Плитка «Отгрузка» на главном экране ТСД. Видны все собранные заказы. Кнопки «Все», «WB», «Ozon» — отбор по площадке.',
      },
      {
        title: 'Отметить заказы',
        text: 'Нажимайте на карточки заказов, которые отдаёте курьеру, или «Выбрать все (N)».',
      },
      {
        title: 'Передать курьеру',
        text: 'Нажмите «Передать курьеру (N)» и подтвердите. Товар спишется со склада, появится отгрузка в разделе «Отгрузки».',
      },
      {
        title: 'Поставка FBO',
        text: 'Плитка «Поставки FBO» открывает страницу поставок: откройте зарезервированную поставку, соберите по «Что взять со склада», разложите по коробам и нажмите «Отгружено».',
      },
    ],
    tips: [
      'Сейчас передача курьеру делается с компьютера: «Заказы FBS → Упаковка» → «Передать курьеру (N)».',
      'Отгрузка у нас не отмечает отгрузку в кабинете: WB — передайте поставку в доставку в кабинете, Ozon — сформируйте отгрузку и акт в кабинете.',
      'Если заказ вернули с пункта приёма, менеджер оформляет это в «Отгрузки» кнопкой «Не принято ПВЗ».',
    ],
  },
  {
    id: 'tsd-stock',
    title: 'ТСД: где товар и перемещение',
    group: 'ТСД и телефон',
    routes: ['/tsd/lookup', '/tsd/move', '/tsd/inventory'],
    roles: 'кладовщик, администратор',
    summary: 'Пока не используется — сборка идёт по бумажному листу. Раздел на будущее. Быстрые складские операции: узнать, где лежит товар, и переложить его в другую ячейку.',
    steps: [
      {
        title: 'Где товар',
        text: 'Плитка «Где товар» → «Отсканируйте товар». Видны артикул, название, «Свободно / резерв» и список ячеек с количеством («зона отгрузки» — собранные заказы).',
      },
      {
        title: 'Перемещение: откуда',
        text: 'Плитка «Перемещение» → «1. Ячейка, откуда берёте» — отсканируйте ячейку.',
      },
      {
        title: 'Перемещение: товар и куда',
        text: '«2. Товар» — отсканируйте товар. «3. Ячейка, куда кладёте» — отсканируйте новую ячейку.',
      },
      {
        title: 'Перемещение: количество',
        text: '«4. Сколько (в ячейке N)» — введите количество (не больше, чем лежит) → «Переместить». Сбросить шаги — «Начать заново».',
      },
    ],
    tips: [
      'Перемещение не меняет общий остаток товара — только ячейку.',
      'Пересчёт склада делается через плитку «Инвентаризация» (документ с комиссией), а не правкой остатка на ТСД.',
    ],
  },
  {
  "id": "catalog-search",
  "title": "Поиск товаров и поставщиков",
  "group": "Работа со складом и справочниками",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Единый поиск в номенклатуре и проверка результата поиска площадки.",
  "steps": [
    {
      "title": "Ищите поставщика в общей строке",
      "text": "В «Товары и комплекты → Номенклатура» введите название, штрихкод, артикул или поставщика в одно поле поиска. Отдельного списка поставщиков больше нет. Категории и переключатели «Всё», «Товары», «Наборы», «Артикул 1С» продолжают ограничивать результат. Очистите лишние фильтры, если товар не найден.",
      "image": "/manual/img/20261103-products.png",
      "imageAlt": "Ищите поставщика в общей строке — экран программы"
    },
    {
      "title": "Проверяйте связь товара с площадкой",
      "text": "В поставке WB вводите баркод или артикул карточки WB; в поставке Ozon — данные Ozon. Поиск в поставке ограничен площадкой и выбранным кабинетом. Сверьте название результата. Если код ведёт к другому товару, проверьте связь в «Связи с WB / Ozon» и карточке товара. Не добавляйте неподходящую позицию.",
      "image": "/manual/img/20261103-composition.png",
      "imageAlt": "Проверяйте связь товара с площадкой — экран программы"
    }
  ],
  "tips": [
    "При выгрузке состава WB выбирается баркод карточки WB, начинающийся с 2. Это не внутренний артикул и не ШК короба."
  ]
},
  {
  "id": "stock-cells",
  "title": "Ячейки и брак в складских документах",
  "group": "Работа со складом и справочниками",
  "routes": [],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Где искать брак и почему доступный остаток не всегда списывается из выбранной ячейки.",
  "steps": [
    {
      "title": "Откройте Брак и проверка",
      "text": "Раздел перенесён: «Остатки → Оприходование и списание → Брак и проверка», рядом со «Списание». Здесь менеджер разбирает брак и неизвестные штрихкоды. В списке приёмок этой вкладки больше нет.",
      "image": "/manual/img/20261103-holds.png",
      "imageAlt": "Откройте Брак и проверка — экран программы"
    },
    {
      "title": "Проверьте автоматически выбранную ячейку",
      "text": "При добавлении товара в оприходование или списание ячейка подбирается автоматически. Проверьте её в строке документа и при необходимости измените. Для списания важен свободный остаток именно в ячейках, а не только общее «Доступно». Резервы и товар в коробах не становятся свободными от ручной замены ячейки.",
      "image": "/manual/img/19_stock_doc_new.png",
      "imageAlt": "Проверьте автоматически выбранную ячейку — экран программы"
    },
    {
      "title": "Сохраните черновик и проверьте проведение",
      "text": "Укажите причину, количество и ячейки, сохраните черновик. Проведение выполняет менеджер. Если программа сообщает нехватку, прочитайте ячейку и товар в ошибке, сравните с «Остатки» и историей движения. Не увеличивайте остаток искусственно для обхода ошибки.",
      "image": "/manual/img/19_stock_doc_new.png",
      "imageAlt": "Сохраните черновик и проверьте проведение — экран программы"
    }
  ],
  "tips": [
    "Автоматическая подстановка не доказывает, что физически товар лежит в этой ячейке. Перед перемещением или списанием сверьте фактическое хранение."
  ]
},
  {
  "id": "supplier-calendar",
  "title": "Календарь заказов поставщикам",
  "group": "Поставки FBO / FBW",
  "routes": [
    "/supplier-calendar"
  ],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Графики заказа, время окончания приёма и напоминания.",
  "steps": [
    {
      "title": "Посмотрите календарь",
      "text": "Нажмите «Открыть календарь» в напоминании или откройте «Календарь заказов». Вкладки «Сегодня» и «Ближайшие 7 дней» показывают дни заказа. Время указано по Минску. Срок доставки по графику нужно подтвердить у поставщика.",
      "image": "/manual/img/20261103-calendar.png",
      "imageAlt": "Посмотрите календарь — экран программы"
    },
    {
      "title": "Настройте график и отметьте результат",
      "text": "Откройте «Графики поставщиков» → «Заполнить график» или «Изменить график». Укажите дни заказа, время окончания приёма и срок доставки, затем сохраните график. «Посмотреть потребность» открывает закупку. «Сегодня не нужно» требует причину; «Вернуть напоминание» возвращает пропущенное действие. Это не отправляет письмо или сообщение поставщику.",
      "image": "/manual/img/20261103-calendar.png",
      "imageAlt": "Настройте график и отметьте результат — экран программы"
    }
  ],
  "tips": [
    "Telegram-настройки — отдельное административное подключение. Настройка графика сама не отправляет заказ поставщику."
  ]
},
  {
  "id": "marking",
  "title": "Электронные накладные и СИ",
  "group": "Работа со складом и справочниками",
  "routes": [
    "/marking"
  ],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Приёмка Bidmart, коды экземпляров и отдельная упаковка маркированных товаров.",
  "steps": [
    {
      "title": "Проверьте электронную накладную",
      "text": "Из «Приёмка» нажмите «Bidmart / СИ». Выберите архив ZIP или исходный XML → «Проверить файл». Проверьте поставщика, соответствие товаров и количество кодов. Не создавайте приёмку, пока остались ошибки сопоставления.",
      "image": "/manual/img/20261103-marking.png",
      "imageAlt": "Проверьте электронную накладную — экран программы"
    },
    {
      "title": "Примите товар и завершите документ",
      "text": "После проверки нажмите «Создать ожидаемую приёмку», откройте документ и выберите ячейку. Для товара с СИ сканируйте код каждого экземпляра; для строки без СИ укажите итог принятого количества. Менеджер завершает приёмку; частичная приёмка требует подтверждения и причины недостачи.",
      "image": "/manual/img/20261103-marking.png",
      "imageAlt": "Примите товар и завершите документ — экран программы"
    },
    {
      "title": "Упакуйте и проверьте СИ",
      "text": "Откройте «Упаковка СИ», выберите партию или заказ. Сканируйте заводской СИ, напечатайте копию для внешней упаковки и выполните контрольное сканирование. Только после комплектности и проверки отметьте «Заказ упакован». Этот раздел помечен в программе как тестовая реализация: внешняя передача сведений не подключена.",
      "image": "/manual/img/20261103-marking.png",
      "imageAlt": "Упакуйте и проверьте СИ — экран программы"
    }
  ],
  "tips": [
    "Не путайте товарные стикеры коробов поставки с заводским СИ конкретного экземпляра."
  ]
},
  {
  "id": "economics-guide",
  "title": "Экономика и создание карточек",
  "group": "Работа со складом и справочниками",
  "routes": [
    "/eco"
  ],
  "roles": "менеджер, администратор; складские действия — согласно роли",
  "summary": "Где находится отдельная инструкция административных инструментов.",
  "steps": [
    {
      "title": "Откройте справку Экономики",
      "text": "В разделе «Экономика» откройте «Как считаем» (/eco/help). Здесь описаны источники цены, комиссий, логистики, хранения, рекламы и формулы расчёта. При отсутствии подтверждённых данных программа показывает «нет данных»: это не нулевая комиссия или логистика.",
      "image": "/manual/img/20261103-economics.png",
      "imageAlt": "Откройте справку Экономики — экран программы"
    },
    {
      "title": "Используйте инструкцию создания карточек",
      "text": "Для создания карточек и комплектов откройте отдельную иллюстрированную инструкцию /manual/card-creation.html. Публикация карточки и выгрузка остатков отличаются от чтения заказов: проверяйте выбранный кабинет, режим и предварительный результат перед подтверждением отправки.",
      "image": "/manual/img/32_products_mpcards.png",
      "imageAlt": "Используйте инструкцию создания карточек — экран программы"
    }
  ],
  "tips": [
    "Финансовые расчёты и создание карточек доступны согласно роли. Не меняйте подключения и режим выгрузки остатков ради просмотра справки."
  ]
},
];
